import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { 
  Search, Users, MapPin, Sparkles, Eye, RefreshCw, Play, Pause, 
  RotateCcw, ZoomIn, ZoomOut, Settings, Filter, Grid, Network,
  MousePointer2, Hand, RotateCw, ChevronUp, ChevronDown, 
  ChevronLeft, ChevronRight, X, Info, BookOpen, Brain, Globe, Zap, Download,
  Upload, FileJson,
  Wrench, Bot, ChevronDown as ChevronDownIcon, ChevronRight as ChevronRightIcon
} from 'lucide-react';
import knowledgeGraphApi from '../services/knowledgeGraphApi';
import englishDataService from '../services/englishDataService';
import a2aEnglishService from '../api/services/a2aEnglishService';
import { downloadKnowledgeGraphHTML } from '../utils/htmlDownloaderSimple';
import LoadingSpinner from './LoadingSpinner';

function KnowledgeGraph() {
  const [inputText, setInputText] = useState(`The Little Weaver and the Whispering Woods
Once upon a time, in a land dusted with starlight and whispered secrets, lived a young girl named Elara. She wasn't a princess or a knight, but a simple weaver, her fingers nimble and her heart kind. She lived with her grandmother in a small cottage on the edge of the Whispering Woods, a place rumored to hold both magic and mystery.
Elara's days were filled with the rhythmic clack of her loom, weaving colorful threads into tapestries that depicted the world around her. Her grandmother, a wise woman with eyes that held the wisdom of the woods, taught her the craft, and also spoke of the importance of kindness. "The woods," she would say, "listen to those who are kind, and their secrets will be revealed."`);

  const [selectedEntity, setSelectedEntity] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('graph'); // 'graph', 'list', 'toolbox', 'ai-generated'
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [selectedSample, setSelectedSample] = useState('weaving_story');
  const [useMockData, setUseMockData] = useState(false); // false = use Supabase/A2A, true = use local mock
  
  // Supabase data states
  const [knowledgeGraphSamples, setKnowledgeGraphSamples] = useState([]);
  const [sampleTexts, setSampleTexts] = useState({ sampleList: [], sampleTexts: {} });
  const [dataLoading, setDataLoading] = useState(true);
  const [dataError, setDataError] = useState(null);
  
  // ToolBox state
  const [toolboxState, setToolboxState] = useState({
    expandedGroups: {
      characters: true,
      places: true,
      objects: true,
      relationships: true
    },
    workspaceEntities: [],
    selectedWorkspaceEntity: null,
    workspaceZoom: 1,
    workspacePan: { x: 0, y: 0 },
    leftPanelCollapsed: false,
    rightPanelVisible: false
  });

  // AI Generated state
  const [aiGeneratedContent, setAiGeneratedContent] = useState({
    analysis: null,
    insights: [],
    recommendations: [],
    isGenerating: false
  });

  // Analysis progress tracking
  const [analysisProgress, setAnalysisProgress] = useState({
    stage: 'idle', // 'idle', 'preparing', 'analyzing', 'processing', 'complete'
    message: '',
    timeElapsed: 0,
    tokensUsed: 0,
    inputTokens: 0,
    outputTokens: 0,
    entitiesFound: 0,
    relationshipsFound: 0,
    startTime: null,
    currentStep: 0,
    totalSteps: 5,
    costRmb: 0,
    pricingPeriod: 'standard' // 'standard' | 'discount'
  });
  // Pricing: deepseek-chat, RMB per 1M tokens
  const getBeijingPricingPeriod = () => {
    const now = new Date();
    // Convert to Beijing time (UTC+8)
    const utc = now.getTime() + now.getTimezoneOffset() * 60000;
    const bj = new Date(utc + 8 * 3600000);
    const minutes = bj.getHours() * 60 + bj.getMinutes();
    // Discount: 00:30–08:30 (inclusive of 00:30, exclusive of 08:31)
    const discountStart = 0 * 60 + 30; // 00:30
    const discountEnd = 8 * 60 + 30;   // 08:30
    return minutes >= discountStart && minutes < discountEnd ? 'discount' : 'standard';
  };

  const calculateDeepseekCostRmb = (inputTokens, outputTokens, { assumeCacheMiss = true } = {}) => {
    const period = getBeijingPricingPeriod();
    // Rates per 1,000,000 tokens (RMB)
    const rates = {
      standard: { inputHit: 0.5, inputMiss: 2, output: 8 },
      discount: { inputHit: 0.25, inputMiss: 1, output: 4 },
    }[period];
    const inputRate = assumeCacheMiss ? rates.inputMiss : rates.inputHit;
    const inputCost = (inputTokens / 1_000_000) * inputRate;
    const outputCost = (outputTokens / 1_000_000) * rates.output;
    const total = inputCost + outputCost;
    return { total, inputCost, outputCost, period };
  };

  const [graphSettings, setGraphSettings] = useState({
    nodeSize: 12,
    linkDistance: 120,
    charge: -400,
    gravity: 0.1,
    friction: 0.9,
    showLabels: true,
    showRelationshipLabels: true,
    animate: true,
    layout: 'force'
  });
  const [filterSettings, setFilterSettings] = useState({
    characters: true,
    places: true,
    objects: true,
    relationships: true
  });
  
  // Responsive device + workspace scaling
  const [deviceType, setDeviceType] = useState('desktop'); // 'mobile' | 'tablet' | 'desktop'
  const [entityScaleBase, setEntityScaleBase] = useState(0.8);
  const [isMobileEditorOpen, setIsMobileEditorOpen] = useState(false);

  const recalcDevice = useCallback(() => {
    const w = window.innerWidth;
    if (w < 768) {
      setDeviceType('mobile');
      setEntityScaleBase(0.85); // make entities larger on small screens
    } else if (w < 1280) {
      setDeviceType('tablet');
      setEntityScaleBase(0.95);
    } else {
      setDeviceType('desktop');
      setEntityScaleBase(1.0); // largest on desktop
    }
  }, []);

  useEffect(() => {
    recalcDevice();
    window.addEventListener('resize', recalcDevice);
    return () => window.removeEventListener('resize', recalcDevice);
  }, [recalcDevice]);

  // Collapse left panel by default on mobile; use right panel on desktop, bottom sheet on mobile/tablet
  useEffect(() => {
    if (deviceType === 'mobile') {
      setToolboxState(prev => ({ ...prev, leftPanelCollapsed: true }));
    }
  }, [deviceType]);

  // Floating window state
  const [floatingWindow, setFloatingWindow] = useState({
    isVisible: false,
    position: { x: 320, y: 180 },
    size: { width: 380, height: 300 },
    isDragging: false,
    isResizing: false,
    dragOffset: { x: 0, y: 0 }
  });
  
  const svgRef = useRef(null);
  const floatingRef = useRef(null);
  const fileInputRef = useRef(null);
  const [graphData, setGraphData] = useState({ nodes: [], links: [] });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState({ x: 0, y: 0, z: 0 });

  // Export current graph as JSON
  const handleExportJson = () => {
    try {
      const exportPayload = {
        inputText,
        analysis: {
          timeElapsed: analysisProgress.timeElapsed,
          tokensUsed: analysisProgress.tokensUsed,
          entitiesFound: analysisProgress.entitiesFound,
          relationshipsFound: analysisProgress.relationshipsFound,
          stage: analysisProgress.stage
        },
        graph: graphData
      };
      const blob = new Blob([JSON.stringify(exportPayload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'knowledge-graph.json';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export JSON', err);
      alert('Failed to export JSON. See console for details.');
    }
  };

  // Import JSON and update state
  const handleImportJsonClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleImportJsonChange = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      // Update input text if provided
      if (json.inputText && typeof json.inputText === 'string') {
        setInputText(json.inputText);
      }
      // Prefer graph format if available, otherwise convert from entities
      if (json.graph && json.graph.nodes && json.graph.links) {
        setGraphData({ nodes: json.graph.nodes, links: json.graph.links });
      } else if (json.nodes && json.links) {
        setGraphData({ nodes: json.nodes, links: json.links });
      } else if (json.entities) {
        const converted = knowledgeGraphApi.convertToGraphData(json);
        setGraphData(converted);
      } else {
        alert('JSON does not contain recognizable graph data.');
      }
    } catch (err) {
      console.error('Failed to import JSON', err);
      alert('Failed to import JSON. Ensure the file is valid.');
    } finally {
      // Reset the input so the same file can be chosen again next time
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [translation, setTranslation] = useState({ x: 0, y: 0 });
  const [isAutoRotating, setIsAutoRotating] = useState(false);
  const autoRotateRef = useRef(null);

  // Auto-rotation effect
  useEffect(() => {
    if (isAutoRotating) {
      autoRotateRef.current = setInterval(() => {
        setRotation(prev => ({
          ...prev,
          z: (prev.z + 1) % 360
        }));
      }, 50);
    } else {
      if (autoRotateRef.current) {
        clearInterval(autoRotateRef.current);
        autoRotateRef.current = null;
      }
    }

    return () => {
      if (autoRotateRef.current) {
        clearInterval(autoRotateRef.current);
      }
    };
  }, [isAutoRotating]);

  // Timer effect for tracking analysis progress
  useEffect(() => {
    let interval;
    if (analysisProgress.stage !== 'idle' && analysisProgress.stage !== 'complete') {
      interval = setInterval(() => {
        setAnalysisProgress(prev => ({
          ...prev,
          timeElapsed: prev.startTime ? Math.floor((Date.now() - prev.startTime) / 1000) : 0
        }));
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [analysisProgress.stage, analysisProgress.startTime]);

  // Analyze text using DeepSeek API or mock data
  // Optional sampleIdOverride ensures we use the just-selected mock sample
  const analyzeText = useCallback(async (text, sampleIdOverride = null) => {
    console.log('analyzeText called with text length:', text.length);
    setIsAnalyzing(true);
    const startTime = Date.now();
    
    // Initialize progress tracking
    setAnalysisProgress({
      stage: 'preparing',
      message: 'Preparing text for analysis...',
      timeElapsed: 0,
      tokensUsed: 0,
      inputTokens: 0,
      outputTokens: 0,
      entitiesFound: 0,
      relationshipsFound: 0,
      startTime: startTime,
      currentStep: 1,
      totalSteps: 5,
      costRmb: 0,
      pricingPeriod: getBeijingPricingPeriod()
    });

    try {
      let result;
      
      if (useMockData) {
        console.log('[KnowledgeGraph] Using mock data for analysis');
        // Simulate analysis steps for mock data
        setAnalysisProgress(prev => ({
          ...prev,
          stage: 'analyzing',
          message: 'Analyzing text content...',
          currentStep: 2
        }));
        
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        setAnalysisProgress(prev => ({
          ...prev,
          stage: 'processing',
          message: 'Extracting entities and relationships...',
          currentStep: 3
        }));
        
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        setAnalysisProgress(prev => ({
          ...prev,
          stage: 'processing',
          message: 'Building knowledge graph structure...',
          currentStep: 4
        }));
        
        await new Promise(resolve => setTimeout(resolve, 800));
        
        // Use data for selected sample (or explicit override)
        const targetId = sampleIdOverride || selectedSample;
        const { getMockDataByStoryId } = require('../data/knowledgeGraphData');
        const data = getMockDataByStoryId(targetId) || getMockDataByStoryId('weaving_story');
        
        if (data) {
          result = data;
        }
        
        // Validate result before accessing properties
        if (!result || !result.entities) {
          throw new Error('Invalid data structure: missing entities');
        }
        
        // Calculate mock statistics
        const entities = result.entities || {};
        const totalEntities = (entities.characters?.length || 0) + 
                            (entities.places?.length || 0) + 
                            (entities.objects?.length || 0);
        const totalRelationships = result.relationships?.length || 0;
        const estimatedTokens = Math.floor(text.length / 4) + 500;
        const timeElapsed = Math.floor((Date.now() - startTime) / 1000);
        
        setAnalysisProgress(prev => ({
          ...prev,
          stage: 'complete',
          message: 'Analysis completed successfully!',
          currentStep: 5,
          entitiesFound: totalEntities,
          relationshipsFound: totalRelationships,
          tokensUsed: estimatedTokens,
          inputTokens: 0,
          outputTokens: 0,
          timeElapsed: timeElapsed,
          costRmb: 0,
          pricingPeriod: getBeijingPricingPeriod()
        }));
        
      } else {
        console.log('[KnowledgeGraph] Using A2A DeepSeek API for analysis');
        // Use A2A analyzeKnowledgeGraph capability
        setAnalysisProgress(prev => ({
          ...prev,
          stage: 'analyzing',
          message: 'Sending text to A2A backend...',
          currentStep: 2
        }));
        
        setAnalysisProgress(prev => ({
          ...prev,
          stage: 'analyzing',
          message: 'Analyzing text content with DeepSeek AI...',
          currentStep: 3
        }));
        
        try {
          console.log('[KnowledgeGraph] Calling A2A analyzeKnowledgeGraph with text length:', text.length);
          const a2aResult = await a2aEnglishService.analyzeKnowledgeGraph(text, {
            extractEntities: true,
            extractRelations: true
          });
          
          console.log('[KnowledgeGraph] A2A response received:', {
            hasResult: !!a2aResult,
            resultKeys: a2aResult ? Object.keys(a2aResult) : [],
            hasData: !!(a2aResult && a2aResult.data),
            hasEntities: !!(a2aResult && a2aResult.entities),
            hasGraph: !!(a2aResult && a2aResult.graph),
            success: a2aResult?.success,
            entitiesCount: a2aResult?.entities?.length || 0,
            relationshipsCount: a2aResult?.relationships?.length || 0,
            fullResult: a2aResult
          });
          
          // Check if the request failed
          if (a2aResult && a2aResult.success === false) {
            throw new Error(a2aResult.error || 'A2A analysis failed');
          }
          
          // Handle A2A response format according to server implementation
          // Server returns: { success: true, entities: [...], relationships: [...], graph: { nodes, links }, metadata: {...} }
          // Use the graph.nodes and graph.links directly like ChineseDaily does
          if (a2aResult) {
            // Transform A2A result to match expected format (like ChineseDaily)
            // Server nodes have 'label' but component expects 'name', and needs x, y, z, radius
            const rawNodes = a2aResult.nodes || a2aResult.graph?.nodes || [];
            const rawLinks = a2aResult.links || a2aResult.graph?.links || [];
            
            // Transform nodes: convert 'label' to 'name' and add required rendering properties
            const nodes = rawNodes.map((node, idx) => ({
              id: node.id || `entity_${idx}`,
              name: node.name || node.label || `Entity ${idx}`,
              type: node.type || 'Unknown',
              description: node.description || '',
              x: node.x !== undefined ? node.x : (Math.cos(idx * 0.5) * 200),
              y: node.y !== undefined ? node.y : (Math.sin(idx * 0.5) * 200),
              z: node.z !== undefined ? node.z : 0,
              radius: node.radius || Math.max(10, (node.importance || 5) * 2.2),
              importance: node.importance || 5
            }));
            
            // Transform links: ensure source and target are node IDs
            const links = rawLinks.map((link, idx) => ({
              id: link.id || `rel_${idx}`,
              source: link.source || link.from || '',
              target: link.target || link.to || '',
              type: link.type || 'related',
              description: link.description || '',
              width: link.width || Math.max(2, (link.strength || 5) / 2),
              strength: link.strength || 5
            }));
            
            const graphData = {
              nodes,
              links,
              entities: a2aResult.entities || [],
              relationships: a2aResult.relationships || [],
              summary: a2aResult.summary || a2aResult.analysis || {}
            };
            
            console.log('[KnowledgeGraph] A2A graph data extracted:', {
              nodesCount: graphData.nodes?.length || 0,
              linksCount: graphData.links?.length || 0,
              entitiesCount: graphData.entities?.length || 0,
              relationshipsCount: graphData.relationships?.length || 0
            });
            
            // Validate that we have at least some nodes
            if (!graphData.nodes || graphData.nodes.length === 0) {
              console.warn('[KnowledgeGraph] No nodes extracted from A2A response');
              throw new Error('No nodes extracted from analysis');
            }
            
            // Set graph data directly (no conversion needed - server already provides nodes/links)
            setGraphData(graphData);
            setIsAnalyzing(false);
            
            // Extract statistics for progress
            const totalEntities = graphData.nodes.length;
            const totalRelationships = graphData.links.length;
            const inputTokens = a2aResult.metadata?.inputTokens || Math.floor(text.length / 6);
            const outputTokens = a2aResult.metadata?.outputTokens || Math.floor(text.length / 8);
            const actualTokens = (a2aResult.metadata?.tokensUsed) || (inputTokens + outputTokens);
            const timeElapsed = Math.floor((Date.now() - startTime) / 1000);
            const { total, period } = calculateDeepseekCostRmb(inputTokens, outputTokens, { assumeCacheMiss: true });
            
            setAnalysisProgress(prev => ({
              ...prev,
              stage: 'complete',
              message: 'Analysis completed successfully!',
              currentStep: 5,
              entitiesFound: totalEntities,
              relationshipsFound: totalRelationships,
              tokensUsed: actualTokens,
              inputTokens,
              outputTokens,
              timeElapsed: timeElapsed,
              costRmb: Number(total.toFixed(6)),
              pricingPeriod: period
            }));
            
            return; // Exit early - we've set the graph data
          } else {
            throw new Error('A2A returned empty result');
          }
        } catch (a2aError) {
          console.error('[KnowledgeGraph] A2A analysis failed:', {
            message: a2aError.message,
            stack: a2aError.stack,
            name: a2aError.name
          });
          console.warn('A2A analysis failed, falling back to direct API:', a2aError);
          // Fallback to direct API
          try {
            result = await knowledgeGraphApi.analyzeTextForKnowledgeGraph(text);
            // Convert fallback result to graph data
            if (result && result.entities) {
              console.log('Converting fallback result to graph data:', result);
              const graphData = knowledgeGraphApi.convertToGraphData(result);
              console.log('Converted fallback graph data:', graphData);
              console.log('Nodes count:', graphData.nodes.length);
              console.log('Links count:', graphData.links.length);
              // Clear then set to force refresh
              setGraphData({ nodes: [], links: [] });
              setTimeout(() => setGraphData(graphData), 0);
            } else {
              throw new Error('Fallback API returned invalid result structure');
            }
          } catch (fallbackError) {
            console.error('[KnowledgeGraph] Fallback API also failed:', fallbackError);
            throw fallbackError;
          }
        }
      }
      
      // For mock data mode, convert result to graph data
      if (useMockData && result && result.entities) {
        console.log('[KnowledgeGraph] Converting mock result to graph data:', result);
        const graphData = knowledgeGraphApi.convertToGraphData(result);
        console.log('[KnowledgeGraph] Converted graph data:', graphData);
        console.log('[KnowledgeGraph] Nodes count:', graphData.nodes.length);
        console.log('[KnowledgeGraph] Links count:', graphData.links.length);
        // Clear then set to force refresh
        setGraphData({ nodes: [], links: [] });
        setTimeout(() => setGraphData(graphData), 0);
      } else if (useMockData && !result) {
        console.error('[KnowledgeGraph] No valid result from mock analysis:', result);
        throw new Error('Analysis returned invalid result structure');
      }
    } catch (error) {
      console.error('Analysis failed:', error);
      
      setAnalysisProgress(prev => ({
        ...prev,
        stage: 'complete',
        message: 'Analysis failed, using fallback data...',
        currentStep: 5
      }));
      
      // Fallback to mock data
      if (useMockData) {
        const { getMockDataByStoryId } = require('../data/knowledgeGraphData');
        const fallbackData = getMockDataByStoryId('weaving_story');
        if (fallbackData) {
          console.log('[KnowledgeGraph] Using fallback mock data:', fallbackData);
          const graphData = knowledgeGraphApi.convertToGraphData(fallbackData);
          console.log('[KnowledgeGraph] Fallback graph data:', graphData);
          setGraphData(graphData);
        }
      }
    } finally {
      setIsAnalyzing(false);
      // Do not auto-hide the progress box; keep it as reference until next run or manual reset
    }
  }, [useMockData, selectedSample]);

  // Expand a sample text to approximately N words by repeating paragraphs
  const expandToApproxWords = (text, targetWords = 1000) => {
    const words = (text || '').trim().split(/\s+/);
    if (words.length >= targetWords) return text;
    const paragraphs = text.split(/\n+/).filter(Boolean);
    let out = text.trim();
    let i = 0;
    while (out.trim().split(/\s+/).length < targetWords) {
      out += `\n\n` + paragraphs[i % paragraphs.length];
      i += 1;
    }
    return out;
  };

  // Helper: Transform Supabase story data to graph format
  // Handles:
  // 1. Direct graph format: {graph: {nodes: [...], links: [...]}} (from JSON files)
  // 2. Supabase format: {entities: {characters: [...], places: [...], objects: [...]}, relationships: [{from, to, ...}]}
  const transformStoryToGraphData = (story) => {
    if (!story) return null;
    
    // Priority 1: Use graph.nodes and graph.links directly if available (from JSON files)
    if (story.graph && story.graph.nodes && Array.isArray(story.graph.nodes) && story.graph.nodes.length > 0) {
      console.log('[KnowledgeGraph] Using direct graph.nodes and graph.links from story');
      return {
        nodes: story.graph.nodes.map(node => ({
          id: node.id || `node_${Math.random()}`,
          name: node.name || node.label || 'Unknown',
          label: node.name || node.label || 'Unknown',
          type: node.type || 'Unknown',
          description: node.description || '',
          x: node.x !== undefined ? node.x : (Math.random() * 200 - 100),
          y: node.y !== undefined ? node.y : (Math.random() * 200 - 100),
          z: node.z !== undefined ? node.z : (Math.random() * 100 - 50),
          radius: node.radius || Math.max(10, (node.importance || 5) * 2.2),
          importance: node.importance || 5,
          tags: node.tags || []
        })),
        links: (story.graph.links || []).map(link => ({
          id: link.id || `link_${Math.random()}`,
          source: link.source || link.from || '',
          target: link.target || link.to || '',
          type: link.type || 'related',
          description: link.description || '',
          width: link.width || Math.max(2, (link.strength || 5) / 2),
          strength: link.strength || 5
        }))
      };
    }
    
    // Priority 2: Construct from entities and relationships (Supabase format)
    console.log('[KnowledgeGraph] Constructing graph from entities and relationships');
    
    // Flatten grouped entities from Supabase format
    let entitiesArray = [];
    if (story.entities) {
      if (Array.isArray(story.entities)) {
        entitiesArray = story.entities;
      } else if (typeof story.entities === 'object') {
        // Database format: {characters: [...], places: [...], objects: [...]}
        entitiesArray = [
          ...(story.entities.characters || []),
          ...(story.entities.places || []),
          ...(story.entities.objects || [])
        ];
      }
    }
    
    // Create nodes from entities
    const nodes = entitiesArray.map((entity, idx) => ({
      id: entity.id || `entity_${idx}`,
      name: entity.name || entity.label || `Entity ${idx}`,
      label: entity.name || entity.label || `Entity ${idx}`,
      type: entity.type || 'Unknown',
      description: entity.description || '',
      x: entity.x !== undefined ? entity.x : (Math.cos(idx * 0.5) * 200),
      y: entity.y !== undefined ? entity.y : (Math.sin(idx * 0.5) * 200),
      z: entity.z !== undefined ? entity.z : (Math.random() * 100 - 50),
      radius: entity.radius || Math.max(10, (entity.importance || 5) * 2.2),
      importance: entity.importance || 5,
      tags: entity.tags || []
    }));
    
    // Create links from relationships - map from/to to source/target
    // Build a set of valid node IDs for validation
    const nodeIds = new Set(nodes.map(n => n.id));
    
    const links = (story.relationships || [])
      .map((rel, idx) => {
        // Map 'from' to 'source' and 'to' to 'target' for Supabase format
        const source = rel.source || rel.from || '';
        const target = rel.target || rel.to || '';
        
        return {
          id: rel.id || `rel_${idx}`,
          source: source,
          target: target,
          type: rel.type || 'related',
          description: rel.description || '',
          width: rel.width || Math.max(2, (rel.strength || 5) / 2),
          strength: rel.strength || 5
        };
      })
      .filter(link => {
        // Only include links where both source and target exist in nodes
        const isValid = nodeIds.has(link.source) && nodeIds.has(link.target);
        if (!isValid) {
          console.warn('[KnowledgeGraph] Filtering out invalid link:', {
            source: link.source,
            target: link.target,
            sourceExists: nodeIds.has(link.source),
            targetExists: nodeIds.has(link.target)
          });
        }
        return isValid;
      });
    
    console.log('[KnowledgeGraph] Transformed data:', {
      nodesCount: nodes.length,
      linksCount: links.length,
      nodeIds: Array.from(nodeIds).slice(0, 5),
      sampleNode: nodes[0],
      sampleLink: links[0]
    });
    
    return { nodes, links };
  };

  // Load sample story; check for pre-existing KG JSON first, then analyze if needed
  // Follows ChineseDaily pattern: try Supabase first, fallback to mock
  const loadSampleStory = async (sampleId) => {
    if (useMockData) {
      // Use local mock data
      const { getStorySampleById } = require('../data/knowledgeGraphData');
      const sample = getStorySampleById(sampleId);
      if (sample) {
        const expanded = expandToApproxWords(sample.content, 1000);
        setInputText(expanded);
        setSelectedSample(sampleId);
        // Will trigger analysis via useEffect
      }
      return;
    }
    
    // Try to fetch from Supabase/A2A first
    try {
      const storyResult = await englishDataService.getKnowledgeGraphStory(sampleId);
      const story = storyResult?.story || storyResult?.data?.story || storyResult;
      
      // Check if story has pre-existing KG JSON data
      if (story && (story.entities || story.relationships || story.graph)) {
        console.log('[KnowledgeGraph] Found pre-existing KG JSON data for story:', sampleId);
        
        // Use pre-existing KG data
        const expanded = expandToApproxWords(story.content || story.text || '', 1000);
        setInputText(expanded);
        setSelectedSample(sampleId);
        
        // Transform using helper function
        const graphData = transformStoryToGraphData(story);
        
        if (graphData && graphData.nodes.length > 0) {
          // Set graph data directly
          setGraphData(graphData);
          
          // Update analysis progress
          setAnalysisProgress({
            stage: 'completed',
            message: 'Loaded pre-existing knowledge graph data',
            timeElapsed: 0,
            tokensUsed: 0,
            entitiesFound: graphData.nodes.length,
            relationshipsFound: graphData.links.length,
            startTime: null,
            currentStep: 5
          });
          
          setIsAnalyzing(false);
          return; // Don't call analyzeText
        }
      }
      
      // No KG data found, fallback to sample text only
      if (story && story.content) {
        const expanded = expandToApproxWords(story.content, 1000);
        setInputText(expanded);
        setSelectedSample(sampleId);
        // Will trigger analysis
      }
    } catch (error) {
      console.warn('[KnowledgeGraph] Failed to get story with KG data, falling back to mock:', error);
      // Fallback to local mock data
      const { getStorySampleById } = require('../data/knowledgeGraphData');
      const sample = getStorySampleById(sampleId);
      if (sample) {
        const expanded = expandToApproxWords(sample.content, 1000);
        setInputText(expanded);
        setSelectedSample(sampleId);
      }
    }
  };

  // Load sample texts via A2A (with Supabase fallback) - follows ChineseDaily pattern
  useEffect(() => {
    const loadSamples = async () => {
      setDataLoading(true);
      try {
        // Try to get stories list from A2A/Supabase
        const a2aResult = await englishDataService.getKnowledgeGraphStories({ limit: 50 });
        
        // Handle different response formats
        let stories = [];
        if (Array.isArray(a2aResult)) {
          stories = a2aResult;
        } else if (a2aResult?.data) {
          stories = Array.isArray(a2aResult.data) ? a2aResult.data : (a2aResult.data.stories || []);
        } else if (a2aResult?.stories) {
          stories = a2aResult.stories;
        }
        
        // Transform to sampleTexts format like ChineseDaily
        // Use story_id as the identifier (matches backend query field)
        const sampleList = stories.map(s => ({
          id: s.story_id || s.id,
          name: s.title || s.name || `Story ${s.story_id || s.id}`
        }));
        
        const sampleTextsObj = {};
        stories.forEach(s => {
          const id = s.story_id || s.id;
          if (id) {
            sampleTextsObj[id] = s.content || s.text || '';
          }
        });
        
        setSampleTexts({ sampleList, sampleTexts: sampleTextsObj });
        setKnowledgeGraphSamples(stories);
        
        // Set default input text if available
        if (sampleTextsObj[selectedSample]) {
          setInputText(sampleTextsObj[selectedSample]);
        } else if (stories.length > 0) {
          const firstId = stories[0].story_id || stories[0].id;
          if (firstId && sampleTextsObj[firstId]) {
            setInputText(sampleTextsObj[firstId]);
            setSelectedSample(firstId);
          }
        }
      } catch (error) {
        console.error('[KnowledgeGraph] Error loading KG data (both A2A and fallback failed):', error);
        // Set empty state so UI doesn't break
        setSampleTexts({ sampleList: [], sampleTexts: {} });
        setKnowledgeGraphSamples([]);
        setDataError(error.message);
      } finally {
        setDataLoading(false);
      }
    };
    
    loadSamples();
  }, []);

  // Initialize with mock data or Supabase data - follows ChineseDaily pattern
  useEffect(() => {
    // Only trigger when useMockData changes OR on initial load with a sample selected
    // Avoid triggering when selectedSample changes to prevent infinite loops
    if (useMockData && selectedSample) {
      const loadMockData = async () => {
        try {
          console.log('[KnowledgeGraph] Loading mock data for:', selectedSample);
          const { getMockDataByStoryId } = require('../data/knowledgeGraphData');
          const mockData = getMockDataByStoryId(selectedSample);
          
          if (mockData) {
            // Transform to graph format
            const graphData = knowledgeGraphApi.convertToGraphData(mockData);
            setGraphData(graphData);
          }
        } catch (error) {
          console.error('[KnowledgeGraph] Error loading mock data:', error);
        }
      };
      loadMockData();
    }
    // Note: Removed the loadSampleStory call from here to avoid infinite loops
    // User must explicitly click "Load Sample Story" or select from dropdown
  }, [useMockData]);

  // Monitor graphData changes (only log in development, and throttle to avoid spam)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      // Only log if data actually changed (not just reference)
      const nodeCount = graphData.nodes?.length || 0;
      const linkCount = graphData.links?.length || 0;
      if (nodeCount > 0 || linkCount > 0) {
        console.log('[KnowledgeGraph] Data updated:', { nodes: nodeCount, links: linkCount });
      }
    }
  }, [graphData.nodes?.length, graphData.links?.length]);

  // Enhanced 3D transformation function - memoized to prevent recreation
  const apply3DTransform = useCallback((x, y, z = 0) => {
    const radX = (rotation.x * Math.PI) / 180;
    const radY = (rotation.y * Math.PI) / 180;
    const radZ = (rotation.z * Math.PI) / 180;

    let newX = x * Math.cos(radY) - z * Math.sin(radY);
    let newZ = x * Math.sin(radY) + z * Math.cos(radY);
    
    let newY = y * Math.cos(radX) - newZ * Math.sin(radX);
    newZ = y * Math.sin(radX) + newZ * Math.cos(radX);
    
    const finalX = newX * Math.cos(radZ) - newY * Math.sin(radZ);
    const finalY = newX * Math.sin(radZ) + newY * Math.cos(radZ);

    const perspective = 600;
    const projectedX = (finalX * perspective) / (perspective + newZ);
    const projectedY = (finalY * perspective) / (perspective + newZ);
    const scale = perspective / (perspective + newZ);

    return { 
      x: projectedX, 
      y: projectedY, 
      scale: Math.max(0.4, scale),
      depth: newZ 
    };
  }, [rotation.x, rotation.y, rotation.z]);

  // Handle entity selection and show floating window
  const handleEntityClick = (entity) => {
    setSelectedEntity(entity);
    setFloatingWindow(prev => ({
      ...prev,
      isVisible: true
    }));
  };

  // Floating window handlers
  const handleFloatingMouseDown = (e) => {
    if (e.target.classList.contains('resize-handle')) {
      setFloatingWindow(prev => ({ ...prev, isResizing: true }));
    } else if (e.target.classList.contains('drag-handle') || e.target.closest('.drag-handle')) {
      const rect = floatingRef.current?.getBoundingClientRect();
      if (rect) {
        setFloatingWindow(prev => ({
          ...prev,
          isDragging: true,
          dragOffset: {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
          }
        }));
      }
    }
  };

  const handleFloatingMouseMove = (e) => {
    if (floatingWindow.isDragging) {
      setFloatingWindow(prev => ({
        ...prev,
        position: {
          x: e.clientX - prev.dragOffset.x,
          y: e.clientY - prev.dragOffset.y
        }
      }));
    } else if (floatingWindow.isResizing) {
      const rect = floatingRef.current?.getBoundingClientRect();
      if (rect) {
        setFloatingWindow(prev => ({
          ...prev,
          size: {
            width: Math.max(300, e.clientX - rect.left + 10),
            height: Math.max(250, e.clientY - rect.top + 10)
          }
        }));
      }
    }
  };

  const handleFloatingMouseUp = () => {
    setFloatingWindow(prev => ({
      ...prev,
      isDragging: false,
      isResizing: false
    }));
  };

  useEffect(() => {
    if (floatingWindow.isDragging || floatingWindow.isResizing) {
      document.addEventListener('mousemove', handleFloatingMouseMove);
      document.addEventListener('mouseup', handleFloatingMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleFloatingMouseMove);
        document.removeEventListener('mouseup', handleFloatingMouseUp);
      };
    }
  }, [floatingWindow.isDragging, floatingWindow.isResizing]);

  // Memoize graphData to prevent unnecessary re-renders
  const memoizedGraphData = useMemo(() => graphData, [
    graphData.nodes?.length,
    graphData.links?.length,
    JSON.stringify(graphData.nodes?.map(n => n.id).sort()),
    JSON.stringify(graphData.links?.map(l => `${l.source}-${l.target}`).sort())
  ]);

  // Memoize renderGraph to prevent unnecessary re-renders
  const renderGraph = useCallback(() => {
    // Remove excessive logging in production
    if (!memoizedGraphData.nodes || !memoizedGraphData.nodes.length) {
      return null;
    }
    
    // Ensure all nodes have required properties for rendering
    const validNodes = memoizedGraphData.nodes.filter(node => {
      const hasRequiredProps = node.id && (node.name || node.label);
      if (!hasRequiredProps && process.env.NODE_ENV === 'development') {
        console.warn('[KnowledgeGraph] Node missing required properties:', node);
      }
      return hasRequiredProps;
    });
    
    if (validNodes.length === 0) {
      return null;
    }

    const width = 800;
    const height = 600;
    const centerX = width / 2;
    const centerY = height / 2;

    const filteredNodes = validNodes.filter(node => {
      if (node.type === 'protagonist' || node.type === 'mentor' || node.type === 'supporting_character' || node.type === 'antagonist' || node.type === 'legendary_character') {
        return filterSettings.characters;
      }
      if (node.type === 'dwelling' || node.type === 'magical_realm' || node.type === 'setting' || node.type === 'landmark' || node.type === 'city' || node.type === 'threat') {
        return filterSettings.places;
      }
      return filterSettings.objects;
    });

    const filteredLinks = filterSettings.relationships ? 
      memoizedGraphData.links.filter(link => 
        filteredNodes.some(n => n.id === link.source) && 
        filteredNodes.some(n => n.id === link.target)
      ) : [];

    const transformedNodes = filteredNodes.map(node => {
      const transformed = apply3DTransform(node.x, node.y, node.z || 0);
      return {
        ...node,
        transformedX: transformed.x,
        transformedY: transformed.y,
        transformedScale: transformed.scale,
        depth: transformed.depth
      };
    });

    const sortedNodes = transformedNodes.sort((a, b) => a.depth - b.depth);

    const getNodeColor = (type) => {
      switch (type) {
        case 'protagonist': return '#10b981'; // Emerald for hero
        case 'mentor': return '#3b82f6'; // Blue for wisdom
        case 'supporting_character': return '#8b5cf6'; // Purple for support
        case 'antagonist': return '#ef4444'; // Red for villain
        case 'legendary_character': return '#f59e0b'; // Amber for legendary
        case 'dwelling': return '#06b6d4'; // Cyan for home
        case 'magical_realm': return '#16a34a'; // Forest green
        case 'setting': return '#8b5cf6'; // Purple for magical land
        case 'landmark': return '#0ea5e9'; // Sky blue for landmarks
        case 'city': return '#6366f1'; // Indigo for cities
        case 'threat': return '#dc2626'; // Red for threats
        case 'craft_tool': return '#dc2626'; // Red for tools
        case 'artwork': return '#eab308'; // Gold for art
        case 'materials': return '#7c3aed'; // Purple for threads
        case 'virtue': return '#ec4899'; // Pink for kindness
        case 'knowledge': return '#0ea5e9'; // Sky blue for wisdom
        case 'mystery': return '#64748b'; // Gray for secrets
        case 'weapon': return '#dc2626'; // Red for weapons
        case 'artifact': return '#f59e0b'; // Amber for artifacts
        case 'symbol': return '#8b5cf6'; // Purple for symbols
        case 'power': return '#f97316'; // Orange for power
        default: return '#6b7280';
      }
    };

    return (
      <g transform={`translate(${centerX + translation.x}, ${centerY + translation.y}) scale(${zoom})`}>
        {/* Links */}
        {filteredLinks.map((link, index) => {
          const sourceNode = sortedNodes.find(n => n.id === link.source);
          const targetNode = sortedNodes.find(n => n.id === link.target);
          if (!sourceNode || !targetNode) return null;

          const dx = targetNode.transformedX - sourceNode.transformedX;
          const dy = targetNode.transformedY - sourceNode.transformedY;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const normalizedDx = dx / distance;
          const normalizedDy = dy / distance;

          const sourceRadius = sourceNode.radius * sourceNode.transformedScale;
          const targetRadius = targetNode.radius * targetNode.transformedScale;

          const startX = sourceNode.transformedX + normalizedDx * sourceRadius;
          const startY = sourceNode.transformedY + normalizedDy * sourceRadius;
          const endX = targetNode.transformedX - normalizedDx * targetRadius;
          const endY = targetNode.transformedY - normalizedDy * targetRadius;

          const avgDepth = (sourceNode.depth + targetNode.depth) / 2;
          const opacity = Math.max(0.3, 1 - avgDepth / 150);

          return (
            <g key={`link-${index}`}>
              <line
                x1={startX}
                y1={startY}
                x2={endX}
                y2={endY}
                stroke="#94a3b8"
                strokeWidth={(link.width * Math.min(sourceNode.transformedScale, targetNode.transformedScale)) / 3}
                strokeOpacity={opacity * 0.7}
                markerEnd="url(#arrowhead)"
              />
              {graphSettings.showRelationshipLabels && distance > 60 && (
                <text
                  x={(startX + endX) / 2}
                  y={(startY + endY) / 2}
                  textAnchor="middle"
                  fontSize={11 * Math.min(sourceNode.transformedScale, targetNode.transformedScale)}
                  fill="#475569"
                  fillOpacity={opacity}
                  className="pointer-events-none select-none font-medium"
                >
                  {link.type}
                </text>
              )}
            </g>
          );
        })}

        {/* Nodes */}
        {sortedNodes.map((node) => {
          const nodeRadius = node.radius * node.transformedScale;
          const opacity = Math.max(0.4, 1 - node.depth / 150);
          
          return (
            <g key={node.id} className="cursor-pointer">
              <circle
                cx={node.transformedX}
                cy={node.transformedY}
                r={nodeRadius}
                fill={getNodeColor(node.type)}
                fillOpacity={opacity}
                stroke={selectedEntity?.id === node.id ? '#1d4ed8' : '#ffffff'}
                strokeWidth={selectedEntity?.id === node.id ? (4 * node.transformedScale) / 3 : (3 * node.transformedScale) / 3}
                className="hover:stroke-indigo-500 transition-all duration-200"
                onClick={() => handleEntityClick(node)}
                style={{
                  filter: node.depth > 0 ? 'brightness(0.85)' : 'brightness(1.15)'
                }}
              />
              {graphSettings.showLabels && nodeRadius > 6 && (
                <text
                  x={node.transformedX}
                  y={node.transformedY + nodeRadius + 18 * node.transformedScale}
                  textAnchor="middle"
                  fontSize={13 * node.transformedScale}
                  fill="#1e293b"
                  fillOpacity={opacity}
                  className="pointer-events-none select-none font-semibold"
                >
                  {node.name}
                </text>
              )}
            </g>
          );
        })}

        <defs>
          <marker
            id="arrowhead"
            markerWidth="10"
            markerHeight="7"
            refX="9"
            refY="3.5"
            orient="auto"
          >
            <polygon
              points="0 0, 10 3.5, 0 7"
              fill="#94a3b8"
            />
          </marker>
        </defs>
      </g>
    );
  }, [
    memoizedGraphData,
    filterSettings.characters,
    filterSettings.places,
    filterSettings.objects,
    filterSettings.relationships,
    rotation.x,
    rotation.y,
    rotation.z,
    translation.x,
    translation.y,
    zoom,
    graphSettings.showLabels,
    graphSettings.showRelationshipLabels,
    selectedEntity?.id,
    apply3DTransform
  ]);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;
      setTranslation(prev => ({
        x: prev.x + deltaX * 0.5,
        y: prev.y + deltaY * 0.5
      }));
      setDragStart({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const resetView = () => {
    setZoom(1);
    setRotation({ x: 0, y: 0, z: 0 });
    setTranslation({ x: 0, y: 0 });
    setIsAutoRotating(false);
  };

  const rotateGraph = (axis, degrees) => {
    setRotation(prev => ({
      ...prev,
      [axis]: (prev[axis] + degrees) % 360
    }));
  };

  // Download HTML function
  const handleDownloadHTML = () => {
    console.log('Download HTML clicked');
    console.log('Current graphData:', graphData);
    console.log('Nodes count:', graphData.nodes?.length || 0);
    console.log('Links count:', graphData.links?.length || 0);
    console.log('Current analysisProgress:', analysisProgress);
    
    if (!graphData || !graphData.nodes || graphData.nodes.length === 0) {
      alert('Please analyze some text first to generate a knowledge graph. The analysis may still be in progress.');
      return;
    }
    
    // Ensure we have valid data before downloading
    const validGraphData = {
      nodes: graphData.nodes.map(node => ({
        ...node,
        x: node.x || 0,
        y: node.y || 0,
        z: node.z || 0,
        radius: node.radius || Math.max(10, (node.importance || 5) * 2.2),
        importance: node.importance || 5
      })),
      links: graphData.links.map(link => ({
        ...link,
        source: link.source,
        target: link.target,
        width: link.width || Math.max(2, (link.strength || 5) / 2),
        strength: link.strength || 5
      }))
    };
    
    console.log('Validated graphData for download:', validGraphData);
    console.log('Analysis data being passed to HTML:', {
      timeElapsed: analysisProgress.timeElapsed,
      tokensUsed: analysisProgress.tokensUsed,
      entitiesFound: analysisProgress.entitiesFound,
      relationshipsFound: analysisProgress.relationshipsFound,
      stage: analysisProgress.stage
    });
    
    downloadKnowledgeGraphHTML(validGraphData, analysisProgress, inputText);
  };

  // Test function to manually trigger analysis and verify data
  const testAnalysis = () => {
    console.log('=== TESTING ANALYSIS ===');
    console.log('Current selectedSample:', selectedSample);
    console.log('Current useMockData:', useMockData);
    
    let data = null;
    if (useMockData) {
      const { getMockDataByStoryId } = require('../data/knowledgeGraphData');
      data = getMockDataByStoryId(selectedSample);
    } else {
      // Try Supabase first, then mock
      data = knowledgeGraphSamples.find(s => s.id === selectedSample);
      if (!data) {
        const { getMockDataByStoryId } = require('../data/knowledgeGraphData');
        data = getMockDataByStoryId(selectedSample);
      }
    }
    
    console.log('Data for selectedSample:', data);
    
    if (data) {
      const convertedData = knowledgeGraphApi.convertToGraphData(data);
      console.log('Converted data:', convertedData);
      console.log('Nodes:', convertedData.nodes.length);
      console.log('Links:', convertedData.links.length);
      
      // Set the data directly for testing
      setGraphData(convertedData);
      console.log('=== TEST COMPLETE ===');
    } else {
      console.log('No data found for selectedSample');
    }
  };

  // Manual reset function for analysis progress
  const resetAnalysisProgress = () => {
    setAnalysisProgress({
      stage: 'idle',
      message: '',
      timeElapsed: 0,
      tokensUsed: 0,
      entitiesFound: 0,
      relationshipsFound: 0,
      startTime: null,
      currentStep: 0
    });
  };

  // AI Analysis Generation
  const generateAIAnalysis = async () => {
    setAiGeneratedContent(prev => ({ ...prev, isGenerating: true }));
    
    // Generate AI analysis based on workspace entities
    const workspaceEntities = toolboxState.workspaceEntities;
    
    setTimeout(() => {
      if (workspaceEntities.length === 0) {
        setAiGeneratedContent(prev => ({
          ...prev,
          analysis: "No entities in workspace. Please add entities to generate analysis.",
          insights: [],
          recommendations: [],
          isGenerating: false
        }));
        return;
      }

      // Analyze workspace entities
      const entityTypes = workspaceEntities.map(e => e.type);
      const entityNames = workspaceEntities.map(e => e.name);
      const relationships = workspaceEntities.filter(e => e.type === 'relationship');
      const characters = workspaceEntities.filter(e => e.type.includes('character'));
      const places = workspaceEntities.filter(e => e.type.includes('dwelling') || e.type.includes('realm') || e.type.includes('setting'));
      const objects = workspaceEntities.filter(e => e.type.includes('tool') || e.type.includes('artwork') || e.type.includes('material'));

      // Generate analysis based on entity composition
      let analysis = "";
      let insights = [];
      let recommendations = [];

      if (characters.length > 0) {
        analysis += `The narrative centers around ${characters.length} key character${characters.length > 1 ? 's' : ''}: ${characters.map(c => c.name).join(', ')}. `;
      }

      if (places.length > 0) {
        analysis += `The story unfolds across ${places.length} distinct location${places.length > 1 ? 's' : ''}: ${places.map(p => p.name).join(', ')}. `;
      }

      if (objects.length > 0) {
        analysis += `Central to the plot are ${objects.length} significant object${objects.length > 1 ? 's' : ''}: ${objects.map(o => o.name).join(', ')}. `;
      }

      if (relationships.length > 0) {
        analysis += `The narrative explores ${relationships.length} key relationship${relationships.length > 1 ? 's' : ''} that drive the plot forward. `;
      }

      analysis = analysis || "Analysis of selected workspace entities reveals a narrative framework with potential for development.";

      // Generate insights based on entity types
      if (characters.length >= 2) {
        insights.push(`Character dynamics between ${characters[0].name} and ${characters[1].name} form the emotional core of the narrative`);
      }
      
      if (places.length > 0 && objects.length > 0) {
        insights.push(`The setting of ${places[0].name} provides context for the significance of ${objects[0].name}`);
      }

      if (relationships.length > 0) {
        insights.push(`Relationship dynamics create tension and drive character development throughout the story`);
      }

      if (workspaceEntities.length >= 3) {
        insights.push(`The selected ${workspaceEntities.length} entities create a balanced narrative structure with multiple thematic elements`);
      }

      // Generate recommendations
      if (characters.length < 2) {
        recommendations.push("Consider adding more characters to create richer interpersonal dynamics");
      }
      
      if (places.length < 2) {
        recommendations.push("Expand the world-building by adding more diverse locations");
      }

      if (objects.length < 2) {
        recommendations.push("Incorporate more symbolic objects to enhance thematic depth");
      }

      recommendations.push("Develop the connections between selected entities to strengthen narrative coherence");
      recommendations.push("Consider how the selected entities interact to create compelling story arcs");

      setAiGeneratedContent(prev => ({
        ...prev,
        analysis,
        insights,
        recommendations,
        isGenerating: false
      }));
    }, 2000);
  };

  // ToolBox Functions
  const handleRunAnalysis = () => {
    setViewMode('ai-generated');
    setTimeout(() => {
      generateAIAnalysis();
      generateEssayFromWorkspace();
    }, 300);
  };

  // Toolbox specific functions
  const handleWorkspaceEntityClick = (entity) => {
    setToolboxState(prev => ({
      ...prev,
      selectedWorkspaceEntity: entity,
      rightPanelVisible: deviceType === 'desktop'
    }));
    if (deviceType !== 'desktop') {
      setIsMobileEditorOpen(true);
    }
  };

  const toggleLeftPanel = () => {
    setToolboxState(prev => ({
      ...prev,
      leftPanelCollapsed: !prev.leftPanelCollapsed
    }));
  };

  const toggleRightPanel = () => {
    setToolboxState(prev => ({
      ...prev,
      rightPanelVisible: !prev.rightPanelVisible
    }));
  };

  const updateWorkspaceEntity = (entityId, updates) => {
    setToolboxState(prev => ({
      ...prev,
      workspaceEntities: prev.workspaceEntities.map(entity => 
        entity.id === entityId ? { ...entity, ...updates } : entity
      ),
      selectedWorkspaceEntity: prev.selectedWorkspaceEntity?.id === entityId 
        ? { ...prev.selectedWorkspaceEntity, ...updates }
        : prev.selectedWorkspaceEntity
    }));
  };

  // Generate essay draft and performance score from workspace entities
  const generateEssayFromWorkspace = () => {
    const entities = toolboxState.workspaceEntities || [];
    if (entities.length === 0) return;

    const characters = entities.filter(e => (e.type || '').includes('character'));
    const places = entities.filter(e => ['dwelling','magical_realm','setting','landmark','city','threat'].includes(e.type));
    const objects = entities.filter(e => ['craft_tool','artwork','materials','virtue','knowledge','mystery','weapon','artifact','symbol','power','relationship'].includes(e.type));

    const charNames = characters.map(c => c.name).join(', ');
    const placeNames = places.map(p => p.name).join(', ');
    const objectNames = objects.map(o => o.name).join(', ');

    const modifiedEssay = [
      `In this revised version, ${charNames || 'the main characters'} journey through ${placeNames || 'the story world'}.`,
      objects.length > 0
        ? `Along the way, they interact with ${objectNames}, which alters the motivation and tension of the narrative.`
        : `Their decisions and encounters reshape the narrative arc in subtle but meaningful ways.`,
      `These changes highlight the interplay between setting and character goals, producing a distinct outcome from the original passage.`
    ].join(' ');

    const used = entities.length;
    const diversity = (characters.length > 0) + (places.length > 0) + (objects.length > 0);
    const score = Math.min(100, Math.round(50 + used * 8 + diversity * 6));

    setAiGeneratedContent(prev => ({
      ...prev,
      essayDraft: modifiedEssay,
      performanceScore: score,
      changeSummary: {
        usedEntities: used,
        characters: characters.length,
        places: places.length,
        objects: objects.length
      }
    }));
  };

  // Handle entity dragging within workspace
  const handleEntityDrag = (e, entityId) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const workspaceRect = e.currentTarget.closest('.workspace-container').getBoundingClientRect();
    
    const newX = e.clientX - workspaceRect.left - rect.width / 2;
    const newY = e.clientY - workspaceRect.top - rect.height / 2;
    
    updateWorkspaceEntity(entityId, {
      position: { x: Math.max(0, newX), y: Math.max(0, newY) }
    });
  };

  const allEntities = [...(graphData.nodes || [])];
  const filteredEntities = allEntities.filter(entity =>
    entity.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    entity.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Show loading state while data is being fetched
  if (dataLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-cyan-50 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner isLoading={true} progress={0} currentStep="Loading knowledge graph data..." />
          <p className="mt-4 text-gray-600">Loading Knowledge Graph data from database...</p>
        </div>
      </div>
    );
  }

  // Show error state if data loading failed
  if (dataError && !useMockData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-cyan-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">⚠️ Error Loading Data</div>
          <p className="text-gray-600 mb-4">{dataError}</p>
          <div className="space-x-4">
            <button 
              onClick={() => window.location.reload()} 
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Retry
            </button>
            <button 
              onClick={() => setUseMockData(true)} 
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              Use Mock Data
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-cyan-50">
      {/* Header */}
      <div className="bg-white shadow-lg border-b border-indigo-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg">
                <Brain className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  Knowledge Graph Analyzer
                </h1>
                <p className="text-sm text-indigo-600 font-medium">Professional visualization and interactive rotation control</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Language Toggle */}
              <div className="flex items-center gap-2 bg-indigo-100 rounded-lg p-1">
                <button className="px-3 py-1 rounded text-sm font-medium bg-white text-indigo-600 shadow-sm">
                  <Globe className="w-4 h-4 inline mr-1" />
                  English
                </button>
                <button className="px-3 py-1 rounded text-sm font-medium text-indigo-700 hover:text-indigo-900">
                  <Globe className="w-4 h-4 inline mr-1" />
                  Chinese
                </button>
              </div>

              {/* View Mode Toggle */}
              <div className="flex items-center gap-2 bg-indigo-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('graph')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'graph' 
                      ? 'bg-white text-indigo-600 shadow-sm' 
                      : 'text-indigo-700 hover:text-indigo-900'
                  }`}
                >
                  <Network className="w-4 h-4 inline mr-2" />
                  Graph View
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'list' 
                      ? 'bg-white text-indigo-600 shadow-sm' 
                      : 'text-indigo-700 hover:text-indigo-900'
                  }`}
                >
                  <Grid className="w-4 h-4 inline mr-2" />
                  Analysis View
                </button>
                <button
                  onClick={() => setViewMode('toolbox')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'toolbox' 
                      ? 'bg-white text-indigo-600 shadow-sm' 
                      : 'text-indigo-700 hover:text-indigo-900'
                  }`}
                >
                  <Wrench className="w-4 h-4 inline mr-2" />
                  ToolBox
                </button>
                <button
                  onClick={() => setViewMode('ai-generated')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'ai-generated' 
                      ? 'bg-white text-indigo-600 shadow-sm' 
                      : 'text-indigo-700 hover:text-indigo-900'
                  }`}
                >
                  <Bot className="w-4 h-4 inline mr-2" />
                  AI Generated
                </button>
              </div>

              {/* Analyze Button */}
              <button
                onClick={() => analyzeText(inputText)}
                disabled={isAnalyzing}
                className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-red-600 to-pink-600 text-white rounded-lg hover:from-red-700 hover:to-pink-700 disabled:opacity-50 transition-all font-medium"
              >
                {isAnalyzing ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4" />
                    Analyze
                  </>
                )}
              </button>

              {/* Test Analysis Button */}
              <button
                onClick={testAnalysis}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-yellow-600 to-orange-600 text-white rounded-lg hover:from-yellow-700 hover:to-orange-700 transition-all font-medium"
                title="Test analysis and data conversion"
              >
                <Zap className="w-4 h-4" />
                Test Analysis
              </button>

              {/* Reset Analysis Button */}
              <button
                onClick={resetAnalysisProgress}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-lg hover:from-gray-700 hover:to-gray-800 transition-all font-medium"
                title="Reset analysis progress"
              >
                <RotateCcw className="w-4 h-4" />
                Reset
              </button>

              {/* Download HTML Button */}
              <button
                onClick={handleDownloadHTML}
                disabled={!graphData || !graphData.nodes || graphData.nodes.length === 0 || isAnalyzing}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 transition-all font-medium"
                title={graphData.nodes.length > 0 ? "Download Knowledge Graph as HTML" : "Analyze text first to enable download"}
              >
                <Download className="w-4 h-4" />
                {graphData.nodes.length > 0 ? `Download HTML (${graphData.nodes.length} entities)` : 'Download HTML'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Text Input Section */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border border-indigo-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-indigo-900 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-indigo-600" />
              Input Text for Analysis
            </h2>
            
            {/* Sample Stories Dropdown */}
            <div className="flex items-center gap-4">
              <select
                value={selectedSample}
                onChange={(e) => {
                  setSelectedSample(e.target.value);
                  const selected = sampleTexts.sampleList.find(s => s.id === e.target.value);
                  if (selected && sampleTexts.sampleTexts[e.target.value]) {
                    setInputText(sampleTexts.sampleTexts[e.target.value]);
                  }
                  loadSampleStory(e.target.value);
                }}
                className="px-3 py-2 border-2 border-indigo-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                disabled={dataLoading}
              >
                {dataLoading ? (
                  <option>Loading samples...</option>
                ) : sampleTexts.sampleList.length > 0 ? (
                  sampleTexts.sampleList.map(sample => (
                    <option key={sample.id} value={sample.id}>
                      {sample.name}
                    </option>
                  ))
                ) : (
                  <option value="weaving_story">Weaving Story (Mock)</option>
                )}
              </select>

              {/* JSON Export / Import Buttons */}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleExportJson}
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm border-2 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                  title="Download current graph as JSON"
                >
                  <FileJson className="w-4 h-4" />
                  JSON
                </button>
                <button
                  type="button"
                  onClick={handleImportJsonClick}
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm border-2 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                  title="Import graph from JSON"
                >
                  <Upload className="w-4 h-4" />
                  Import
                </button>
                <input
                  type="file"
                  accept="application/json,.json"
                  ref={fileInputRef}
                  onChange={handleImportJsonChange}
                  className="hidden"
                />
              </div>
              
              {/* Mock Data Toggle */}
              <div className="flex items-center gap-2 text-sm">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={useMockData}
                    onChange={(e) => setUseMockData(e.target.checked)}
                    className="rounded border-indigo-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="ml-2 text-indigo-700 font-medium">Use Mock Data</span>
                </label>
              </div>
            </div>
          </div>
          
          <textarea
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            className="w-full h-32 p-4 border-2 border-indigo-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none text-gray-700"
            placeholder="Enter your text here for knowledge graph extraction and analysis..."
          />
          
          {/* Analysis Progress Display */}
          {(analysisProgress.stage !== 'idle') && (
            <div className="mt-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border-2 border-indigo-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-indigo-900 flex items-center gap-2">
                  <RefreshCw className={`w-5 h-5 ${isAnalyzing ? 'animate-spin' : ''}`} />
                  Analysis Progress
                </h3>
              <div className="flex items-center gap-4 text-sm flex-wrap">
                <span className="text-indigo-600 font-medium">
                  Time: {analysisProgress.timeElapsed}s
                </span>
                {analysisProgress.tokensUsed > 0 && (
                  <span className="text-purple-600 font-medium">
                    Tokens: {analysisProgress.tokensUsed}
                  </span>
                )}
                {analysisProgress.tokensUsed > 0 && (
                  <span className="text-emerald-700 font-medium">
                    Cost: ¥{analysisProgress.costRmb} ({analysisProgress.pricingPeriod})
                  </span>
                )}
                {analysisProgress.tokensUsed > 0 && (
                  <button
                    className="px-2 py-1 text-xs rounded border border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                    onClick={() => setAnalysisProgress(prev => ({ ...prev, stage: 'idle', message: '' }))}
                  >
                    Hide
                  </button>
                )}
              </div>
              </div>
              
              {/* Progress Bar */}
              <div className="mb-3">
                <div className="flex justify-between text-xs text-indigo-600 mb-1">
                  <span>Step {analysisProgress.currentStep} of {analysisProgress.totalSteps}</span>
                  <span>{Math.round((analysisProgress.currentStep / analysisProgress.totalSteps) * 100)}%</span>
                </div>
                <div className="w-full bg-indigo-200 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-indigo-500 to-purple-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(analysisProgress.currentStep / analysisProgress.totalSteps) * 100}%` }}
                  ></div>
                </div>
              </div>
              
              {/* Status Message */}
              <div className="mb-3">
                <p className="text-indigo-700 font-medium">{analysisProgress.message}</p>
              </div>
              
              {/* Statistics */}
              {analysisProgress.stage === 'complete' && (
                <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
                  <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
                    <div className="text-2xl font-bold text-emerald-600">{analysisProgress.entitiesFound}</div>
                    <div className="text-xs text-emerald-700">Entities Found</div>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                    <div className="text-2xl font-bold text-blue-600">{analysisProgress.relationshipsFound}</div>
                    <div className="text-xs text-blue-700">Relationships</div>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                    <div className="text-2xl font-bold text-purple-600">{analysisProgress.tokensUsed}</div>
                    <div className="text-xs text-purple-700">Tokens Used</div>
                  </div>
                  <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-200">
                    <div className="text-2xl font-bold text-indigo-600">{analysisProgress.timeElapsed}s</div>
                    <div className="text-xs text-indigo-700">Total Time</div>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                    <div className="text-2xl font-bold text-amber-600">{analysisProgress.inputTokens}</div>
                    <div className="text-xs text-amber-700">Input Tokens</div>
                  </div>
                  <div className="bg-rose-50 rounded-lg p-3 border border-rose-200">
                    <div className="text-2xl font-bold text-rose-600">{analysisProgress.outputTokens}</div>
                    <div className="text-xs text-rose-700">Output Tokens</div>
                  </div>
                </div>
              )}
              
              {/* Stage Indicators */}
              <div className="flex items-center justify-center gap-2 mt-3">
                {['preparing', 'analyzing', 'processing', 'complete'].map((stage, index) => (
                  <div key={stage} className="flex items-center gap-1">
                    <div className={`w-2 h-2 rounded-full ${
                      analysisProgress.stage === stage 
                        ? 'bg-indigo-500 animate-pulse' 
                        : analysisProgress.stage === 'complete' || ['preparing', 'analyzing', 'processing'].indexOf(analysisProgress.stage) > index
                        ? 'bg-green-500'
                        : 'bg-gray-300'
                    }`}></div>
                    <span className={`text-xs font-medium ${
                      analysisProgress.stage === stage 
                        ? 'text-indigo-600' 
                        : analysisProgress.stage === 'complete' || ['preparing', 'analyzing', 'processing'].indexOf(analysisProgress.stage) > index
                        ? 'text-green-600'
                        : 'text-gray-400'
                    }`}>
                      {stage.charAt(0).toUpperCase() + stage.slice(1)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {viewMode === 'graph' ? (
          /* Graph Visualization Mode */
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
            {/* Graph Controls */}
            <div className="xl:col-span-1 space-y-4">
              {/* Search */}
              <div className="bg-white rounded-xl shadow-lg p-4 border border-indigo-200">
                <h3 className="font-semibold text-indigo-900 mb-3 flex items-center gap-2">
                  <Search className="w-4 h-4" />
                  Search & Filter
                </h3>
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search entities..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border-2 border-indigo-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Main Graph Visualization */}
            <div className="xl:col-span-3">
              <div className="bg-white rounded-xl shadow-lg p-6 border border-indigo-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-indigo-900 flex items-center gap-2">
                    <Network className="w-5 h-5 text-indigo-600" />
                    Knowledge Graph Visualization
                  </h3>
                  <div className="flex items-center gap-4 text-sm text-indigo-600">
                    <div className="flex items-center gap-1">
                      <Hand className="w-4 h-4" />
                      <span>Drag to pan</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <MousePointer2 className="w-4 h-4" />
                      <span>Click to select</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <RotateCw className="w-4 h-4" />
                      <span>Use arrows to rotate</span>
                    </div>
                  </div>
                </div>
                
                <div className="relative border-2 border-indigo-300 rounded-lg overflow-hidden bg-gradient-to-br from-indigo-100 via-purple-100 to-cyan-100">
                  {/* Top-Left: Filter Controls Panel */}
                  <div className="absolute top-4 left-4 z-20 bg-white/95 backdrop-blur-sm rounded-lg shadow-lg p-3 border-2 border-indigo-200">
                    <h4 className="font-semibold text-sm text-indigo-900 mb-2 flex items-center gap-1">
                      <Filter className="w-3 h-3" />
                      Entity Filters
                    </h4>
                    <div className="space-y-2">
                      <label className="flex items-center text-xs">
                        <input
                          type="checkbox"
                          checked={filterSettings.characters}
                          onChange={(e) => setFilterSettings(prev => ({
                            ...prev,
                            characters: e.target.checked
                          }))}
                          className="rounded border-indigo-300 text-indigo-600 focus:ring-indigo-500 w-3 h-3"
                        />
                        <span className="ml-1 text-indigo-700 font-medium">Characters</span>
                      </label>
                      <label className="flex items-center text-xs">
                        <input
                          type="checkbox"
                          checked={filterSettings.places}
                          onChange={(e) => setFilterSettings(prev => ({
                            ...prev,
                            places: e.target.checked
                          }))}
                          className="rounded border-indigo-300 text-indigo-600 focus:ring-indigo-500 w-3 h-3"
                        />
                        <span className="ml-1 text-indigo-700 font-medium">Places</span>
                      </label>
                      <label className="flex items-center text-xs">
                        <input
                          type="checkbox"
                          checked={filterSettings.objects}
                          onChange={(e) => setFilterSettings(prev => ({
                            ...prev,
                            objects: e.target.checked
                          }))}
                          className="rounded border-indigo-300 text-indigo-600 focus:ring-indigo-500 w-3 h-3"
                        />
                        <span className="ml-1 text-indigo-700 font-medium">Objects</span>
                      </label>
                      <label className="flex items-center text-xs">
                        <input
                          type="checkbox"
                          checked={filterSettings.relationships}
                          onChange={(e) => setFilterSettings(prev => ({
                            ...prev,
                            relationships: e.target.checked
                          }))}
                          className="rounded border-indigo-300 text-indigo-600 focus:ring-indigo-500 w-3 h-3"
                        />
                        <span className="ml-1 text-indigo-700 font-medium">Relationships</span>
                      </label>
                    </div>
                  </div>

                  {/* Bottom-Left: View Controls Panel */}
                  <div className="absolute bottom-4 left-4 z-20 bg-white/95 backdrop-blur-sm rounded-lg shadow-lg p-3 border-2 border-indigo-200">
                    <h4 className="font-semibold text-sm text-indigo-900 mb-2 flex items-center gap-1">
                      <Settings className="w-3 h-3" />
                      View Controls
                    </h4>
                    
                    {/* Auto Rotate Toggle */}
                    <button
                      onClick={() => setIsAutoRotating(!isAutoRotating)}
                      className={`w-full flex items-center justify-center gap-1 px-2 py-1 rounded text-xs transition-colors mb-2 ${
                        isAutoRotating 
                          ? 'bg-indigo-600 text-white' 
                          : 'bg-indigo-100 hover:bg-indigo-200 text-indigo-700'
                      }`}
                    >
                      {isAutoRotating ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                      {isAutoRotating ? 'Stop Auto Rotate' : 'Auto Rotate'}
                    </button>

                    {/* Zoom Controls */}
                    <div className="flex items-center gap-1 mb-2">
                      <button
                        onClick={() => setZoom(prev => Math.min(prev + 0.2, 3))}
                        className="p-1 bg-indigo-100 hover:bg-indigo-200 rounded transition-colors"
                      >
                        <ZoomIn className="w-3 h-3 text-indigo-700" />
                      </button>
                      <span className="text-xs text-indigo-600 flex-1 text-center min-w-12 font-medium">
                        {Math.round(zoom * 100)}%
                      </span>
                      <button
                        onClick={() => setZoom(prev => Math.max(prev - 0.2, 0.2))}
                        className="p-1 bg-indigo-100 hover:bg-indigo-200 rounded transition-colors"
                      >
                        <ZoomOut className="w-3 h-3 text-indigo-700" />
                      </button>
                    </div>

                    {/* Reset View */}
                    <button
                      onClick={resetView}
                      className="w-full flex items-center justify-center gap-1 px-2 py-1 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded transition-colors text-xs font-medium"
                    >
                      <RotateCcw className="w-3 h-3" />
                      Reset All
                    </button>

                    {/* Display Options */}
                    <div className="space-y-1 mt-2">
                      <label className="flex items-center text-xs">
                        <input
                          type="checkbox"
                          checked={graphSettings.showLabels}
                          onChange={(e) => setGraphSettings(prev => ({
                            ...prev,
                            showLabels: e.target.checked
                          }))}
                          className="rounded border-indigo-300 text-indigo-600 focus:ring-indigo-500 w-3 h-3"
                        />
                        <span className="ml-1 text-indigo-700 font-medium">Show Labels</span>
                      </label>
                      <label className="flex items-center text-xs">
                        <input
                          type="checkbox"
                          checked={graphSettings.showRelationshipLabels}
                          onChange={(e) => setGraphSettings(prev => ({
                            ...prev,
                            showRelationshipLabels: e.target.checked
                          }))}
                          className="rounded border-indigo-300 text-indigo-600 focus:ring-indigo-500 w-3 h-3"
                        />
                        <span className="ml-1 text-indigo-700 font-medium">Relationship Labels</span>
                      </label>
                    </div>
                  </div>

                  {/* Rotation Arrow Controls */}
                  <div className="absolute top-2 left-1/2 transform -translate-x-1/2 z-10">
                    <button
                      onClick={() => rotateGraph('x', -15)}
                      className="p-2 bg-white/90 hover:bg-white shadow-lg rounded-lg transition-all duration-200 hover:scale-110 border border-indigo-200"
                      title="Rotate Up (X-axis -15°)"
                    >
                      <ChevronUp className="w-5 h-5 text-indigo-700" />
                    </button>
                  </div>

                  <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 z-10">
                    <button
                      onClick={() => rotateGraph('x', 15)}
                      className="p-2 bg-white/90 hover:bg-white shadow-lg rounded-lg transition-all duration-200 hover:scale-110 border border-indigo-200"
                      title="Rotate Down (X-axis +15°)"
                    >
                      <ChevronDown className="w-5 h-5 text-indigo-700" />
                    </button>
                  </div>

                  <div className="absolute left-2 top-1/2 transform -translate-y-1/2 z-10">
                    <button
                      onClick={() => rotateGraph('y', -15)}
                      className="p-2 bg-white/90 hover:bg-white shadow-lg rounded-lg transition-all duration-200 hover:scale-110 border border-indigo-200"
                      title="Rotate Left (Y-axis -15°)"
                    >
                      <ChevronLeft className="w-5 h-5 text-indigo-700" />
                    </button>
                  </div>

                  <div className="absolute right-2 top-1/2 transform -translate-y-1/2 z-10">
                    <button
                      onClick={() => rotateGraph('y', 15)}
                      className="p-2 bg-white/90 hover:bg-white shadow-lg rounded-lg transition-all duration-200 hover:scale-110 border border-indigo-200"
                      title="Rotate Right (Y-axis +15°)"
                    >
                      <ChevronRight className="w-5 h-5 text-indigo-700" />
                    </button>
                  </div>

                  <div className="absolute top-2 right-2 z-10 flex gap-1">
                    <button
                      onClick={() => rotateGraph('z', -15)}
                      className="p-2 bg-white/90 hover:bg-white shadow-lg rounded-lg transition-all duration-200 hover:scale-110 border border-indigo-200"
                      title="Rotate Counter-clockwise (Z-axis -15°)"
                    >
                      <RotateCcw className="w-4 h-4 text-indigo-700" />
                    </button>
                    <button
                      onClick={() => rotateGraph('z', 15)}
                      className="p-2 bg-white/90 hover:bg-white shadow-lg rounded-lg transition-all duration-200 hover:scale-110 border border-indigo-200"
                      title="Rotate Clockwise (Z-axis +15°)"
                    >
                      <RotateCw className="w-4 h-4 text-indigo-700" />
                    </button>
                  </div>

                  {/* SVG Canvas */}
                  {graphData.nodes.length > 0 ? (
                    <svg
                      ref={svgRef}
                      width="100%"
                      height="600"
                      viewBox="0 0 800 600"
                      className="cursor-grab active:cursor-grabbing"
                      onMouseDown={handleMouseDown}
                      onMouseMove={handleMouseMove}
                      onMouseUp={handleMouseUp}
                      onMouseLeave={handleMouseUp}
                      onWheel={(e) => {
                        e.preventDefault();
                        const delta = e.deltaY > 0 ? -0.1 : 0.1;
                        setZoom(prev => Math.max(0.2, Math.min(3, prev + delta)));
                      }}
                    >
                      {renderGraph()}
                    </svg>
                  ) : (
                    <div className="flex items-center justify-center h-96 text-center">
                      <div>
                        <div className="text-6xl mb-4">📊</div>
                        <h3 className="text-xl font-semibold text-gray-700 mb-2">No Knowledge Graph Data</h3>
                        <p className="text-gray-500 mb-4">
                          {isAnalyzing ? 'Analyzing text...' : 'Please analyze some text to generate a knowledge graph.'}
                        </p>
                        {!isAnalyzing && (
                          <button
                            onClick={() => {
                              // Always use first available sample from database if available
                              if (!useMockData && sampleTexts.sampleList.length > 0) {
                                console.log('[KnowledgeGraph] Loading first sample from database:', sampleTexts.sampleList[0]);
                                loadSampleStory(sampleTexts.sampleList[0].id);
                              } else if (useMockData) {
                                console.log('[KnowledgeGraph] Loading mock sample: weaving_story');
                                loadSampleStory('weaving_story');
                              } else {
                                console.warn('[KnowledgeGraph] No samples available, falling back to mock');
                                loadSampleStory('weaving_story');
                              }
                            }}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                          >
                            Load Sample Story
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Rotation Status Display */}
                <div className="mt-4 flex items-center justify-center gap-6 text-sm text-indigo-600 font-medium">
                  <span>X: {rotation.x}°</span>
                  <span>Y: {rotation.y}°</span>
                  <span>Z: {rotation.z}°</span>
                  <span>Zoom: {Math.round(zoom * 100)}%</span>
                  {isAutoRotating && (
                    <span className="flex items-center gap-1 text-indigo-600">
                      <RotateCw className="w-4 h-4 animate-spin" />
                      Auto-rotating
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* List View Mode */
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-3 bg-white rounded-xl shadow-lg p-6 border border-indigo-200">
              <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search entities..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border-2 border-indigo-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Entity Categories */}
            <div className="lg:col-span-2 space-y-6">
              {[
                { 
                  key: 'characters', 
                  label: 'Characters',
                  types: ['protagonist', 'mentor', 'supporting_character', 'antagonist', 'legendary_character'], 
                  icon: Users,
                  color: 'emerald'
                },
                { 
                  key: 'places', 
                  label: 'Places',
                  types: ['dwelling', 'magical_realm', 'setting', 'landmark', 'city', 'threat'], 
                  icon: MapPin,
                  color: 'blue'
                },
                { 
                  key: 'objects', 
                  label: 'Objects & Concepts',
                  types: ['craft_tool', 'artwork', 'materials', 'virtue', 'knowledge', 'mystery', 'weapon', 'artifact', 'symbol', 'power'], 
                  icon: Sparkles,
                  color: 'purple'
                }
              ].map(category => {
                const categoryEntities = filteredEntities.filter(entity => 
                  category.types.includes(entity.type)
                );

                if (categoryEntities.length === 0) return null;

                const Icon = category.icon;

                return (
                  <div key={category.key} className={`bg-white rounded-xl shadow-lg p-6 border border-${category.color}-200`}>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Icon className="w-5 h-5 text-indigo-600" />
                      {category.label}
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {categoryEntities.map((entity) => (
                        <div
                          key={entity.id}
                          onClick={() => handleEntityClick(entity)}
                          className={`p-4 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md ${
                            selectedEntity?.id === entity.id
                              ? 'ring-2 ring-indigo-500 ring-offset-2 border-indigo-200 bg-indigo-50'
                              : 'border-gray-200 hover:border-indigo-300 bg-white'
                          }`}
                        >
                          <h4 className="font-semibold text-sm text-gray-800">{entity.name}</h4>
                          <p className="text-xs mt-1 text-gray-600">{entity.description}</p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="inline-block px-2 py-1 text-xs rounded-full bg-indigo-100 text-indigo-800 font-medium">
                              {entity.type.replace('_', ' ')}
                            </span>
                            <span className="text-xs text-gray-500 font-medium">
                              Importance: {entity.importance}/10
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Entity Details */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-indigo-200">
              <h3 className="text-xl font-semibold text-indigo-900 mb-4 flex items-center gap-2">
                <Info className="w-5 h-5" />
                {selectedEntity ? 'Entity Analysis' : 'Select an Entity'}
              </h3>
              
              {selectedEntity ? (
                <div className="space-y-4">
                  <div className="p-4 rounded-lg border border-indigo-200 bg-indigo-50">
                    <h4 className="font-bold text-lg text-indigo-900">{selectedEntity.name}</h4>
                    <p className="text-sm text-indigo-700 mt-1">{selectedEntity.description}</p>
                    <div className="flex items-center justify-between mt-3">
                      <span className="inline-block px-2 py-1 text-xs rounded-full bg-indigo-200 text-indigo-800 font-medium">
                        {selectedEntity.type.replace('_', ' ')}
                      </span>
                      <span className="text-sm text-indigo-600 font-medium">
                        Importance: {selectedEntity.importance}/10
                      </span>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-indigo-900 mb-3">Relationships</h4>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {graphData.links
                        .filter(link => link.source === selectedEntity.id || link.target === selectedEntity.id)
                        .map((relation, index) => {
                          const relatedId = relation.source === selectedEntity.id ? relation.target : relation.source;
                          const relatedEntity = allEntities.find(e => e.id === relatedId);
                          const isOutgoing = relation.source === selectedEntity.id;

                          return (
                            <div key={index} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                              <div className="flex items-center gap-2 text-sm">
                                <span className="font-medium text-indigo-900">
                                  {isOutgoing ? selectedEntity.name : relatedEntity?.name}
                                </span>
                                <span className="text-indigo-600 italic font-medium">{relation.type}</span>
                                <span className="font-medium text-indigo-900">
                                  {isOutgoing ? relatedEntity?.name : selectedEntity.name}
                                </span>
                              </div>
                              <div className="flex items-center justify-between mt-2">
                                <p className="text-xs text-gray-600">{relatedEntity?.description}</p>
                                <span className="text-xs text-gray-500">
                                  Strength: {relation.strength}/10
                                </span>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center text-indigo-600 py-8">
                  <Eye className="w-12 h-12 mx-auto mb-3 opacity-60" />
                  <p>Click on any entity to view detailed analysis and relationships</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ToolBox View Mode */}
        {viewMode === 'toolbox' && (
          <div className={`grid gap-6 ${toolboxState.leftPanelCollapsed ? 'grid-cols-1' : toolboxState.rightPanelVisible ? 'grid-cols-1 xl:grid-cols-3' : 'grid-cols-1 xl:grid-cols-4'}`}>
            {/* ToolBox Panel */}
            <div className={`${toolboxState.leftPanelCollapsed ? 'hidden' : 'xl:col-span-1'}`}>
              <div className="bg-white rounded-xl shadow-lg p-4 border border-indigo-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Wrench className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">ToolBox</h3>
                      <p className="text-sm text-gray-600">Mathematical Tools</p>
                    </div>
                  </div>
                  <button 
                    onClick={toggleLeftPanel}
                    className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100"
                    title="Collapse Panel"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                </div>

                {/* Expand/Collapse Controls */}
                <div className="flex gap-2 mb-4">
                  <button
                    onClick={() => setToolboxState(prev => ({
                      ...prev,
                      expandedGroups: {
                        characters: true,
                        places: true,
                        objects: true,
                        relationships: true
                      }
                    }))}
                    className="flex items-center gap-1 px-3 py-1 bg-green-100 hover:bg-green-200 text-green-700 rounded text-sm font-medium transition-colors"
                  >
                    <ChevronDownIcon className="w-3 h-3" />
                    Expand All
                  </button>
                  <button
                    onClick={() => setToolboxState(prev => ({
                      ...prev,
                      expandedGroups: {
                        characters: false,
                        places: false,
                        objects: false,
                        relationships: false
                      }
                    }))}
                    className="flex items-center gap-1 px-3 py-1 bg-orange-100 hover:bg-orange-200 text-orange-700 rounded text-sm font-medium transition-colors"
                  >
                    <ChevronRightIcon className="w-3 h-3" />
                    Collapse All
                  </button>
                </div>

                {/* Entity Groups */}
                {[
                  {
                    key: 'characters',
                    label: 'Characters',
                    icon: Users,
                    color: 'emerald',
                    entities: graphData.nodes.filter(n => 
                      ['protagonist', 'mentor', 'supporting_character', 'antagonist', 'legendary_character'].includes(n.type)
                    )
                  },
                  {
                    key: 'places',
                    label: 'Places',
                    icon: MapPin,
                    color: 'blue',
                    entities: graphData.nodes.filter(n => 
                      ['dwelling', 'magical_realm', 'setting', 'landmark', 'city', 'threat'].includes(n.type)
                    )
                  },
                  {
                    key: 'objects',
                    label: 'Objects',
                    icon: Sparkles,
                    color: 'purple',
                    entities: graphData.nodes.filter(n => 
                      ['craft_tool', 'artwork', 'materials', 'virtue', 'knowledge', 'mystery', 'weapon', 'artifact', 'symbol', 'power'].includes(n.type)
                    )
                  },
                  {
                    key: 'relationships',
                    label: 'Relationships',
                    icon: Network,
                    color: 'indigo',
                    entities: graphData.links.map((link, index) => ({
                      id: `rel_${index}`,
                      name: `${link.source} ${link.type} ${link.target}`,
                      type: 'relationship',
                      description: `Relationship between ${link.source} and ${link.target}`,
                      importance: link.strength,
                      source: link.source,
                      target: link.target,
                      relationshipType: link.type
                    }))
                  }
                ].map(group => {
                  const Icon = group.icon;
                  const isExpanded = toolboxState.expandedGroups[group.key];
                  
                  return (
                    <div key={group.key} className="mb-4">
                      <div
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData('text/plain', JSON.stringify({
                            type: 'group',
                            group: group.key,
                            entities: group.entities
                          }));
                        }}
                        onClick={() => setToolboxState(prev => ({
                          ...prev,
                          expandedGroups: {
                            ...prev.expandedGroups,
                            [group.key]: !prev.expandedGroups[group.key]
                          }
                        }))}
                        className="flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg cursor-move transition-colors border-2 border-transparent hover:border-indigo-300"
                      >
                        <div className="flex items-center gap-2">
                          <div className={`p-2 bg-${group.color}-100 rounded-lg`}>
                            <Icon className={`w-4 h-4 text-${group.color}-600`} />
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900">{group.label}</h4>
                            <p className="text-xs text-gray-600">{group.entities.length} items</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-xs text-indigo-600 font-medium">Drag to workspace</div>
                          {isExpanded ? (
                            <ChevronDownIcon className="w-4 h-4 text-gray-500" />
                          ) : (
                            <ChevronRightIcon className="w-4 h-4 text-gray-500" />
                          )}
                        </div>
                      </div>
                      
                                             {isExpanded && (
                         <div className="mt-2 space-y-2">
                           {/* Individual Entities */}
                           {group.entities.map(entity => (
                             <div
                               key={entity.id}
                               draggable
                               onDragStart={(e) => {
                                 e.dataTransfer.setData('text/plain', JSON.stringify({
                                   type: 'entity',
                                   entity: entity,
                                   group: group.key
                                 }));
                               }}
                               className="p-2 bg-white border border-gray-200 rounded cursor-move hover:bg-gray-50 transition-colors"
                             >
                               <div className="text-sm font-medium text-gray-900">{entity.name}</div>
                               <div className="text-xs text-gray-600">{entity.description}</div>
                             </div>
                           ))}
                         </div>
                       )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Workspace */}
            <div className={`${toolboxState.rightPanelVisible ? 'xl:col-span-2' : 'xl:col-span-3'}`}>
              <div className="bg-white rounded-xl shadow-lg p-6 border border-indigo-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <h3 className="text-lg font-semibold text-indigo-900 flex items-center gap-2">
                      <Grid className="w-5 h-5 text-indigo-600" />
                      Workspace
                    </h3>
                    {toolboxState.leftPanelCollapsed && (
                      <button
                        onClick={toggleLeftPanel}
                        className="flex items-center gap-1 px-3 py-1 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 rounded text-sm font-medium transition-colors"
                        title="Expand Panel"
                      >
                        <ChevronRight className="w-4 h-4" />
                        Show Toolbox
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setToolboxState(prev => ({ ...prev, workspaceZoom: Math.min(prev.workspaceZoom + 0.1, 2) }))}
                      className="p-2 bg-indigo-100 hover:bg-indigo-200 rounded transition-colors"
                    >
                      <ZoomIn className="w-4 h-4 text-indigo-700" />
                    </button>
                    <span className="text-sm text-indigo-600 font-medium">
                      {Math.round(toolboxState.workspaceZoom * 100)}%
                    </span>
                    <button
                      onClick={() => setToolboxState(prev => ({ ...prev, workspaceZoom: Math.max(prev.workspaceZoom - 0.1, 0.5) }))}
                      className="p-2 bg-indigo-100 hover:bg-indigo-200 rounded transition-colors"
                    >
                      <ZoomOut className="w-4 h-4 text-indigo-700" />
                    </button>
                    <button
                      onClick={() => setToolboxState(prev => ({ 
                        ...prev, 
                        workspaceEntities: [],
                        workspacePan: { x: 0, y: 0 },
                        workspaceZoom: 1
                      }))}
                      className="px-3 py-1 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded text-sm font-medium transition-colors"
                    >
                      Reset
                    </button>
                    <button
                      onClick={() => handleRunAnalysis()}
                      disabled={toolboxState.workspaceEntities.length === 0}
                      className="px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 transition-all font-medium flex items-center gap-2"
                    >
                      <Zap className="w-4 h-4" />
                      RUN
                    </button>
                  </div>
                </div>
                
                <div 
                   className="relative border-2 border-dashed border-indigo-300 rounded-lg bg-gradient-to-br from-indigo-50 via-purple-50 to-cyan-50 min-h-[500px] overflow-hidden workspace-container"
                   onDrop={(e) => {
                     e.preventDefault();
                     const data = JSON.parse(e.dataTransfer.getData('text/plain'));
                     
                     const calculateCascadePosition = (index, totalEntities) => {
                       const baseX = 50;
                       const baseY = 50;
                       const offsetX = 20;
                       const offsetY = 15;
                       return {
                         x: baseX + (index * offsetX),
                         y: baseY + (index * offsetY)
                       };
                     };
                     
                     if (data.type === 'entity') {
                       const currentCount = toolboxState.workspaceEntities.length;
                       const newPosition = calculateCascadePosition(currentCount, currentCount + 1);
                       
                       setToolboxState(prev => ({
                         ...prev,
                         workspaceEntities: [...prev.workspaceEntities, {
                           ...data.entity,
                           position: newPosition,
                           id: `${data.entity.id}_${Date.now()}`
                         }]
                       }));
                     } else if (data.type === 'group') {
                       // Add all entities from the group to workspace in cascade order
                       const currentCount = toolboxState.workspaceEntities.length;
                       const workspaceEntities = data.entities.map((entity, index) => {
                         const newPosition = calculateCascadePosition(currentCount + index, currentCount + data.entities.length);
                         return {
                           ...entity,
                           position: newPosition,
                           id: `${entity.id}_${Date.now()}_${Math.random()}`
                         };
                       });
                       
                       setToolboxState(prev => ({
                         ...prev,
                         workspaceEntities: [...prev.workspaceEntities, ...workspaceEntities]
                       }));
                     }
                   }}
                   onDragOver={(e) => e.preventDefault()}
                 >
                  {toolboxState.workspaceEntities.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center">
                      <Grid className="w-16 h-16 text-indigo-300 mb-4" />
                      <h3 className="text-xl font-semibold text-indigo-600 mb-2">Welcome to Your Workspace</h3>
                      <p className="text-indigo-500 max-w-md">
                        Drag mathematical tools from the left panel to start building your analysis. 
                        Each tool can be positioned, resized, and configured.
                      </p>
                      <div className="flex gap-4 mt-4">
                        <div className="flex items-center gap-1 text-sm text-indigo-600">
                          <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                          <span>Click and drag to pan</span>
                        </div>
                        <div className="flex items-center gap-1 text-sm text-indigo-600">
                          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                          <span>Use zoom controls</span>
                        </div>
                      </div>
                    </div>
                                     ) : (
                     <div className="relative w-full h-full">
                       {/* SVG for connections */}
                        <svg className="absolute inset-0 w-full h-full pointer-events-none">
                         {toolboxState.workspaceEntities.map((entity, index) => {
                           // Find related entities in the workspace
                           const relatedEntities = toolboxState.workspaceEntities.filter(otherEntity => {
                             if (otherEntity.id === entity.id) return false;
                             // Check if there's a relationship in the original graph data
                             return graphData.links.some(link => 
                               (link.source === entity.id && link.target === otherEntity.id) ||
                               (link.target === entity.id && link.source === otherEntity.id)
                             );
                           });
                           
                           return relatedEntities.map((relatedEntity, relIndex) => {
                             const startX = entity.position.x + 100;
                             const startY = entity.position.y + 30;
                             const endX = relatedEntity.position.x + 100;
                             const endY = relatedEntity.position.y + 30;
                             
                             return (
                               <line
                                 key={`${entity.id}-${relatedEntity.id}-${relIndex}`}
                                 x1={startX}
                                 y1={startY}
                                 x2={endX}
                                 y2={endY}
                                 stroke="#6366f1"
                                 strokeWidth="2"
                                 strokeDasharray="5,5"
                                 opacity="0.6"
                               />
                             );
                           });
                         })}
                       </svg>
                       
                       {/* Entity nodes */}
                        {toolboxState.workspaceEntities.map((entity, index) => (
                         <div
                           key={entity.id}
                           onClick={() => handleWorkspaceEntityClick(entity)}
                           draggable
                           onDragStart={(e) => {
                             e.stopPropagation();
                             e.dataTransfer.setData('text/plain', JSON.stringify({
                               type: 'move_entity',
                               entityId: entity.id
                             }));
                           }}
                           onDrag={(e) => {
                             if (e.clientX !== 0 && e.clientY !== 0) {
                               handleEntityDrag(e, entity.id);
                             }
                           }}
                           onDragOver={(e) => e.preventDefault()}
                           onDrop={(e) => {
                             e.preventDefault();
                             e.stopPropagation();
                             const data = JSON.parse(e.dataTransfer.getData('text/plain'));
                             if (data.type === 'move_entity' && data.entityId !== entity.id) {
                               // Swap positions
                               const draggedEntity = toolboxState.workspaceEntities.find(e => e.id === data.entityId);
                               const currentEntity = toolboxState.workspaceEntities.find(e => e.id === entity.id);
                               if (draggedEntity && currentEntity) {
                                 setToolboxState(prev => ({
                                   ...prev,
                                   workspaceEntities: prev.workspaceEntities.map(e => {
                                     if (e.id === data.entityId) {
                                       return { ...e, position: currentEntity.position };
                                     } else if (e.id === entity.id) {
                                       return { ...e, position: draggedEntity.position };
                                     }
                                     return e;
                                   })
                                 }));
                               }
                             }
                           }}
                           className={`absolute bg-white border-2 rounded-lg shadow-lg cursor-move hover:shadow-xl transition-all ${
                             toolboxState.selectedWorkspaceEntity?.id === entity.id 
                               ? 'border-indigo-500 bg-indigo-50' 
                               : 'border-indigo-200 hover:border-indigo-300'
                           }`}
                            style={{
                              left: entity.position.x,
                              top: entity.position.y,
                              transform: `scale(${toolboxState.workspaceZoom * (deviceType === 'desktop' ? 0.7 : deviceType === 'tablet' ? 0.8 : 0.95) * entityScaleBase})`,
                              width: deviceType === 'desktop' ? '160px' : deviceType === 'tablet' ? '140px' : '120px',
                              height: deviceType === 'desktop' ? '110px' : deviceType === 'tablet' ? '95px' : '80px',
                              zIndex: index + 1
                            }}
                         >
                            <div className="text-xs md:text-sm font-medium text-gray-900 truncate">{entity.name}</div>
                            <div className="text-xs text-gray-600 truncate" style={{ fontSize: deviceType === 'desktop' ? '11px' : '10px' }}>{entity.description}</div>
                           <div className="mt-1">
                              <span className="inline-block px-1 py-0.5 text-[10px] rounded-full bg-indigo-100 text-indigo-800 font-medium">
                               {entity.type.replace('_', ' ')}
                             </span>
                           </div>
                         </div>
                       ))}
                     </div>
                   )}
                </div>
              </div>
            </div>

            {/* Right Panel - Entity Details and Modification */}
            {toolboxState.rightPanelVisible && (
              <div className="xl:col-span-1">
                <div className="bg-white rounded-xl shadow-lg p-4 border border-indigo-200">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="p-2 bg-indigo-100 rounded-lg">
                        <Settings className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Entity Details</h3>
                        <p className="text-sm text-gray-600">Modify Parameters</p>
                      </div>
                    </div>
                    <button 
                      onClick={toggleRightPanel}
                      className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100"
                      title="Hide Panel"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  {toolboxState.selectedWorkspaceEntity ? (
                    <div className="space-y-4">
                      {/* Entity Information */}
                      <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                        <h4 className="font-semibold text-indigo-900 mb-2">Entity Information</h4>
                        <div className="space-y-2">
                          <div>
                            <label className="block text-xs font-medium text-indigo-700 mb-1">Name</label>
                            <input
                              type="text"
                              value={toolboxState.selectedWorkspaceEntity.name}
                              onChange={(e) => updateWorkspaceEntity(toolboxState.selectedWorkspaceEntity.id, { name: e.target.value })}
                              className="w-full px-2 py-1 text-sm border border-indigo-200 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-indigo-700 mb-1">Description</label>
                            <textarea
                              value={toolboxState.selectedWorkspaceEntity.description}
                              onChange={(e) => updateWorkspaceEntity(toolboxState.selectedWorkspaceEntity.id, { description: e.target.value })}
                              rows="3"
                              className="w-full px-2 py-1 text-sm border border-indigo-200 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-indigo-700 mb-1">Type</label>
                            <select
                              value={toolboxState.selectedWorkspaceEntity.type}
                              onChange={(e) => updateWorkspaceEntity(toolboxState.selectedWorkspaceEntity.id, { type: e.target.value })}
                              className="w-full px-2 py-1 text-sm border border-indigo-200 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                            >
                              <option value="protagonist">Protagonist</option>
                              <option value="mentor">Mentor</option>
                              <option value="supporting_character">Supporting Character</option>
                              <option value="antagonist">Antagonist</option>
                              <option value="dwelling">Dwelling</option>
                              <option value="magical_realm">Magical Realm</option>
                              <option value="setting">Setting</option>
                              <option value="craft_tool">Craft Tool</option>
                              <option value="artwork">Artwork</option>
                              <option value="materials">Materials</option>
                              <option value="virtue">Virtue</option>
                              <option value="knowledge">Knowledge</option>
                              <option value="mystery">Mystery</option>
                              <option value="relationship">Relationship</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-indigo-700 mb-1">Importance (1-10)</label>
                            <input
                              type="number"
                              min="1"
                              max="10"
                              value={toolboxState.selectedWorkspaceEntity.importance || 5}
                              onChange={(e) => updateWorkspaceEntity(toolboxState.selectedWorkspaceEntity.id, { importance: parseInt(e.target.value) })}
                              className="w-full px-2 py-1 text-sm border border-indigo-200 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Position Controls */}
                      <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                        <h4 className="font-semibold text-purple-900 mb-2">Position</h4>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs font-medium text-purple-700 mb-1">X Position</label>
                            <input
                              type="number"
                              value={Math.round(toolboxState.selectedWorkspaceEntity.position?.x || 0)}
                              onChange={(e) => updateWorkspaceEntity(toolboxState.selectedWorkspaceEntity.id, { 
                                position: { 
                                  ...toolboxState.selectedWorkspaceEntity.position, 
                                  x: parseInt(e.target.value) 
                                } 
                              })}
                              className="w-full px-2 py-1 text-sm border border-purple-200 rounded focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-purple-700 mb-1">Y Position</label>
                            <input
                              type="number"
                              value={Math.round(toolboxState.selectedWorkspaceEntity.position?.y || 0)}
                              onChange={(e) => updateWorkspaceEntity(toolboxState.selectedWorkspaceEntity.id, { 
                                position: { 
                                  ...toolboxState.selectedWorkspaceEntity.position, 
                                  y: parseInt(e.target.value) 
                                } 
                              })}
                              className="w-full px-2 py-1 text-sm border border-purple-200 rounded focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="space-y-2">
                        <button
                          onClick={() => {
                            const entity = toolboxState.selectedWorkspaceEntity;
                            const newPosition = {
                              x: Math.random() * 400 + 50,
                              y: Math.random() * 300 + 50
                            };
                            updateWorkspaceEntity(entity.id, { position: newPosition });
                          }}
                          className="w-full px-3 py-2 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 rounded text-sm font-medium transition-colors"
                        >
                          Randomize Position
                        </button>
                        <button
                          onClick={() => {
                            setToolboxState(prev => ({
                              ...prev,
                              workspaceEntities: prev.workspaceEntities.filter(e => e.id !== toolboxState.selectedWorkspaceEntity.id),
                              selectedWorkspaceEntity: null,
                              rightPanelVisible: false
                            }));
                          }}
                          className="w-full px-3 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded text-sm font-medium transition-colors"
                        >
                          Remove Entity
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Settings className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500 text-sm">Click on an entity in the workspace to modify its properties</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* AI Generated View Mode */}
        {viewMode === 'ai-generated' && (
          <div className="bg-white rounded-xl shadow-lg p-6 border border-indigo-200">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg">
                  <Bot className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">AI Generated Analysis</h2>
                  <p className="text-sm text-gray-600">Advanced insights and recommendations based on your knowledge graph</p>
                </div>
              </div>
              <button
                onClick={() => generateAIAnalysis()}
                disabled={aiGeneratedContent.isGenerating}
                className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 transition-all font-medium"
              >
                {aiGeneratedContent.isGenerating ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Bot className="w-4 h-4" />
                    Generate Analysis
                  </>
                )}
              </button>
            </div>

            {aiGeneratedContent.essayDraft && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                <div className="lg:col-span-2 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg p-6 border border-emerald-200">
                  <h3 className="text-lg font-semibold text-emerald-900 mb-3">Generated Essay Draft</h3>
                  <p className="text-emerald-700 whitespace-pre-wrap">{aiGeneratedContent.essayDraft}</p>
                </div>
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-6 border border-indigo-200">
                  <h3 className="text-lg font-semibold text-indigo-900 mb-3">Performance Feedback</h3>
                  <div className="text-3xl font-bold text-indigo-700 mb-1">{aiGeneratedContent.performanceScore || 0}</div>
                  <div className="text-sm text-indigo-700 mb-3">Score</div>
                  <ul className="text-sm text-indigo-700 space-y-1">
                    <li>Entities used: {aiGeneratedContent.changeSummary?.usedEntities}</li>
                    <li>Characters: {aiGeneratedContent.changeSummary?.characters}</li>
                    <li>Places: {aiGeneratedContent.changeSummary?.places}</li>
                    <li>Objects: {aiGeneratedContent.changeSummary?.objects}</li>
                  </ul>
                </div>
              </div>
            )}

            {aiGeneratedContent.analysis ? (
              <div className="space-y-6">
                {/* Analysis Summary */}
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-6 border border-purple-200">
                  <h3 className="text-lg font-semibold text-purple-900 mb-3">Analysis Summary</h3>
                  <p className="text-purple-700">{aiGeneratedContent.analysis}</p>
                </div>

                {/* Insights */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white rounded-lg p-6 border border-indigo-200">
                    <h3 className="text-lg font-semibold text-indigo-900 mb-4 flex items-center gap-2">
                      <Eye className="w-5 h-5 text-indigo-600" />
                      Key Insights
                    </h3>
                    <div className="space-y-3">
                      {aiGeneratedContent.insights.map((insight, index) => (
                        <div key={index} className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                          <p className="text-indigo-700 text-sm">{insight}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-6 border border-emerald-200">
                    <h3 className="text-lg font-semibold text-emerald-900 mb-4 flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-emerald-600" />
                      Recommendations
                    </h3>
                    <div className="space-y-3">
                      {aiGeneratedContent.recommendations.map((recommendation, index) => (
                        <div key={index} className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                          <p className="text-emerald-700 text-sm">{recommendation}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <Bot className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">No Analysis Generated Yet</h3>
                <p className="text-gray-500 max-w-md mx-auto">
                  Click "Generate Analysis" to create AI-powered insights and recommendations based on your knowledge graph data.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Floating Entity Details Window */}
        {floatingWindow.isVisible && selectedEntity && (
          <div
            ref={floatingRef}
            className="fixed bg-white rounded-lg shadow-2xl border border-indigo-300 z-50"
            style={{
              left: floatingWindow.position.x,
              top: floatingWindow.position.y,
              width: floatingWindow.size.width,
              height: floatingWindow.size.height,
              minWidth: '300px',
              minHeight: '250px'
            }}
            onMouseDown={handleFloatingMouseDown}
          >
            {/* Header with drag handle */}
            <div className="drag-handle flex items-center justify-between p-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-t-lg cursor-move">
              <div className="flex items-center gap-2">
                <Info className="w-4 h-4" />
                <h4 className="font-semibold text-sm">Entity Details</h4>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setFloatingWindow(prev => ({ ...prev, isVisible: false }))}
                  className="p-1 hover:bg-indigo-700 rounded transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-4 h-full overflow-auto" style={{ height: 'calc(100% - 48px)' }}>
              <div className="space-y-3">
                <div>
                  <h5 className="font-bold text-lg text-indigo-900">{selectedEntity.name}</h5>
                  <p className="text-sm text-indigo-700 mt-1">{selectedEntity.description}</p>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="inline-block px-2 py-1 text-xs rounded-full bg-indigo-200 text-indigo-800 font-medium">
                    {selectedEntity.type.replace('_', ' ')}
                  </span>
                  <span className="text-sm text-indigo-600">
                    Importance: {selectedEntity.importance}/10
                  </span>
                </div>

                <div>
                  <h6 className="font-semibold text-indigo-900 mb-2 text-sm">Relationships</h6>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {graphData.links
                      .filter(link => link.source === selectedEntity.id || link.target === selectedEntity.id)
                      .map((relation, index) => {
                        const relatedId = relation.source === selectedEntity.id ? relation.target : relation.source;
                        const relatedEntity = allEntities.find(e => e.id === relatedId);
                        const isOutgoing = relation.source === selectedEntity.id;

                        return (
                          <div key={index} className="p-2 bg-indigo-50 rounded border border-indigo-200">
                            <div className="flex items-center gap-1 text-xs">
                              <span className="font-medium text-indigo-900">
                                {isOutgoing ? selectedEntity.name : relatedEntity?.name}
                              </span>
                              <span className="text-indigo-600">{relation.type}</span>
                              <span className="font-medium text-indigo-900">
                                {isOutgoing ? relatedEntity?.name : selectedEntity.name}
                              </span>
                            </div>
                            <div className="text-xs text-indigo-500 mt-1">
                              Strength: {relation.strength}/10
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </div>
            </div>

            {/* Resize handle */}
            <div className="resize-handle absolute bottom-0 right-0 w-3 h-3 cursor-se-resize">
              <div className="absolute bottom-1 right-1 w-2 h-2 border-r-2 border-b-2 border-indigo-400"></div>
            </div>
          </div>
        )}

        {/* Mobile/Tablet bottom sheet editor */}
        {isMobileEditorOpen && deviceType !== 'desktop' && toolboxState.selectedWorkspaceEntity && (
          <div className="fixed inset-0 z-50 flex items-end bg-black/30" onClick={() => setIsMobileEditorOpen(false)}>
            <div className="w-full bg-white rounded-t-2xl shadow-2xl p-4 max-h-[70vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-semibold text-gray-900">Entity Details</h3>
                <button className="text-gray-500" onClick={() => setIsMobileEditorOpen(false)}>
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-indigo-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={toolboxState.selectedWorkspaceEntity.name}
                    onChange={(e) => updateWorkspaceEntity(toolboxState.selectedWorkspaceEntity.id, { name: e.target.value })}
                    className="w-full px-2 py-1 text-sm border border-indigo-200 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-indigo-700 mb-1">Description</label>
                  <textarea
                    rows="3"
                    value={toolboxState.selectedWorkspaceEntity.description}
                    onChange={(e) => updateWorkspaceEntity(toolboxState.selectedWorkspaceEntity.id, { description: e.target.value })}
                    className="w-full px-2 py-1 text-sm border border-indigo-200 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-indigo-700 mb-1">Type</label>
                  <select
                    value={toolboxState.selectedWorkspaceEntity.type}
                    onChange={(e) => updateWorkspaceEntity(toolboxState.selectedWorkspaceEntity.id, { type: e.target.value })}
                    className="w-full px-2 py-1 text-sm border border-indigo-200 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="protagonist">Protagonist</option>
                    <option value="mentor">Mentor</option>
                    <option value="supporting_character">Supporting Character</option>
                    <option value="antagonist">Antagonist</option>
                    <option value="dwelling">Dwelling</option>
                    <option value="magical_realm">Magical Realm</option>
                    <option value="setting">Setting</option>
                    <option value="craft_tool">Craft Tool</option>
                    <option value="artwork">Artwork</option>
                    <option value="materials">Materials</option>
                    <option value="virtue">Virtue</option>
                    <option value="knowledge">Knowledge</option>
                    <option value="mystery">Mystery</option>
                    <option value="relationship">Relationship</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-purple-700 mb-1">X Position</label>
                    <input
                      type="number"
                      value={Math.round(toolboxState.selectedWorkspaceEntity.position?.x || 0)}
                      onChange={(e) => updateWorkspaceEntity(toolboxState.selectedWorkspaceEntity.id, { position: { ...toolboxState.selectedWorkspaceEntity.position, x: parseInt(e.target.value) } })}
                      className="w-full px-2 py-1 text-sm border border-purple-200 rounded focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-purple-700 mb-1">Y Position</label>
                    <input
                      type="number"
                      value={Math.round(toolboxState.selectedWorkspaceEntity.position?.y || 0)}
                      onChange={(e) => updateWorkspaceEntity(toolboxState.selectedWorkspaceEntity.id, { position: { ...toolboxState.selectedWorkspaceEntity.position, y: parseInt(e.target.value) } })}
                      className="w-full px-2 py-1 text-sm border border-purple-200 rounded focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Statistics Dashboard */}
        <div className="mt-8 bg-white rounded-xl shadow-lg p-6 border border-indigo-200">
          <h3 className="text-xl font-semibold text-indigo-900 mb-4">Knowledge Graph Statistics</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-emerald-50 rounded-lg border border-emerald-200">
              <div className="text-2xl font-bold text-emerald-600">
                {graphData.nodes.filter(n => ['protagonist', 'mentor', 'supporting_character', 'antagonist', 'legendary_character'].includes(n.type)).length}
              </div>
              <div className="text-sm text-emerald-700">Characters</div>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="text-2xl font-bold text-blue-600">
                {graphData.nodes.filter(n => ['dwelling', 'magical_realm', 'setting', 'landmark', 'city', 'threat'].includes(n.type)).length}
              </div>
              <div className="text-sm text-blue-700">Places</div>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg border border-purple-200">
              <div className="text-2xl font-bold text-purple-600">
                {graphData.nodes.filter(n => ['craft_tool', 'artwork', 'materials', 'virtue', 'knowledge', 'mystery', 'weapon', 'artifact', 'symbol', 'power'].includes(n.type)).length}
              </div>
              <div className="text-sm text-purple-700">Concepts</div>
            </div>
            <div className="text-center p-4 bg-indigo-50 rounded-lg border border-indigo-200">
              <div className="text-2xl font-bold text-indigo-600">{graphData.links.length}</div>
              <div className="text-sm text-indigo-700">Relationships</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default KnowledgeGraph;